import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { ThumbsUp, Bookmark, UserRound } from "lucide-react";
import { toast } from "sonner";
import { FacebookSelector } from "@charkour/react-reactions";
import "leaflet/dist/leaflet.css";

import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import Map from "../../components/Map";
import CommentsSection from "../community/components/comment-section/CommentsSection";
import wowImg from "../../assets/images/Community_emojis/wow.png";
import hahaImg from "../../assets/images/Community_emojis/haha.png";
import loveImg from "../../assets/images/Community_emojis/Heart.png";
import angryImg from "../../assets/images/Community_emojis/angry.png";
import sadImg from "../../assets/images/Community_emojis/sad.png";

import { getViewProfile } from "../../redux/slices/profileSlice";
import { getHospitalData } from "../../redux/slices/comparison.Slice";
import {
  getBookmarks,
  filterCity,
  getCitySpotlight,
  searchBookmark,
  checkFilterCityCount,
} from "../../redux/slices/searchSlice";
import CityDetailLimitModal from "../restriction-modals/CityDetailLimitModal";
import RestrictionModal from "../restriction-modals/RestrictionModal";
import jobViewService from "../../api/jobViewService";
import {
  getPersonalizedJobs,
  getJobsbyStateAndCityData,
} from "../../redux/slices/jobsSlice";
import communityService from "../../api/communityService";
import { mapPostsFromAPI } from "../community/adapters/postAdapter";
import {
  getFullImageUrl,
  sanitizePreviewContent,
  getVisibleTextLength,
} from "../community/utils/communityHelpers";
import mortgageScenarioService from "../../api/mortgageScenarioService";
import {
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import usePageTracking from "../../hooks/usePageTracking";
import usePlan from "../../hooks/usePlan";
import useTour from "../../components/tour/useTour";
import GuidedTour from "../../components/tour/GuidedTour";
import getDashboardTourSteps from "./dashboardTourSteps";

import "./dashboard.scss";
import { FiX } from "react-icons/fi";
import parse from "html-react-parser";
import Linkify from "linkify-react";
import {
  PlanShield,
  UpgradePlanModal,
} from "../../components/subscription/UpgradePlanModal";
import {
  PROFILE_COMPLETION_FIELDS,
  getProfileCompletionPercent,
} from "../../utils/profileCompletion";

const PROFILE_COMPLETION_FIELDS_BY_KEY = Object.fromEntries(
  PROFILE_COMPLETION_FIELDS.map((field) => [field.key, field]),
);

// Priority-ordered: the first of these still incomplete is the one shown,
// since it's the field most likely to affect the dashboard's personalization.
const DASHBOARD_RELEVANT_PROFILE_NUDGES = [
  {
    key: "role",
    message: "Add your nursing role to unlock more accurate insights.",
  },
  {
    key: "city",
    message: "Add your city and state to unlock more accurate insights.",
  },
  {
    key: "experience",
    message: "Add your years of experience to unlock more accurate insights.",
  },
  {
    key: "salary",
    message: "Add your preferred salary to unlock more accurate insights.",
  },
];

/* Long → short profession label map (used in the welcome meta line). */
const PROFESSION_SHORT_LABELS = {
  "Home Health and Personal Care Aides": "HHA/PCA",
  "Licensed Practical and Licensed Vocational Nurses": "LPN/LVN",
  "Medical and Health Services Managers": "MHSM",
  "Nurse Anesthetists": "CRNA",
  "Nurse Midwives": "CNM",
  "Nurse Practitioners": "NP",
  "Nursing Assistants": "CNA",
  "Nursing Instructors and Teachers Postsecondary": "Nursing Faculty",
  "Registered Nurses": "RN",
};
const shortRole = (label) => PROFESSION_SHORT_LABELS[label] || label || "NA";

/* Long → short specialty label map. Unmapped specialties display as-is. */
const SPECIALTY_SHORT_LABELS = {
  "Emergency Room (ER)": "ER",
  "Operating Room (OR)": "OR",
  "Cardiac Care Unit (CCU)": "CCU",
  "Medical-Surgical": "Med-Surg",
  "Case Management": "Case Mgmt",
  "Wound Care": "Wound",
};
const shortSpecialty = (label) =>
  SPECIALTY_SHORT_LABELS[label] || label || "NA";

/* Maps a rotating job badge label (e.g. "Trending", "Hospital") to a
   pill palette class. Unknown badges fall back to the success look. */
const JOB_BADGE_CLASSES = {
  Trending: "badge-warning",
  Hospital: "badge-role",
  New: "badge-success",
  Featured: "badge-warning",
  Popular: "badge-warning",
};
const jobBadgeClass = (label) => JOB_BADGE_CLASSES[label] || "badge-success";

/* Compact currency formatter — 1000 → "$1K", 812534 → "$813K",
   1_500_000 → "$1.5M", 1_000_000_000 → "$1B". For sub-$1K values it
   falls back to a plain dollar amount with thousands separators.
   Decimals are dropped past the 10-unit threshold (e.g. 12.3M → "$12M")
   so the strings stay compact. */
const formatCompactCurrency = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "$0";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) {
    const x = abs / 1_000_000_000;
    const s =
      x >= 10 ? String(Math.round(x)) : x.toFixed(1).replace(/\.0$/, "");
    return `${sign}$${s}B`;
  }
  if (abs >= 1_000_000) {
    const x = abs / 1_000_000;
    const s =
      x >= 10 ? String(Math.round(x)) : x.toFixed(1).replace(/\.0$/, "");
    return `${sign}$${s}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${Math.round(abs / 1_000)}K`;
  }
  return `${sign}$${Math.round(abs).toLocaleString("en-US")}`;
};

/* Compact plain-number formatter — 8_804_190 → "8.8M", 126090 → "126K",
   840 → "840". Same thresholds as the currency formatter, without the
   dollar sign. Used for things like city population. */
const formatCompactNumber = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) {
    const x = abs / 1_000_000;
    const s = x >= 10 ? String(Math.round(x)) : x.toFixed(1).replace(/\.0$/, "");
    return `${s}M`;
  }
  if (abs >= 1_000) {
    return `${Math.round(abs / 1_000)}K`;
  }
  return `${Math.round(abs).toLocaleString("en-US")}`;
};

/* ════════════════════════════════════════════════════════════
   Mock data — mirrors the standalone HTML's window.MMP_DATA
   ════════════════════════════════════════════════════════════ */
const MMP_DATA = {
  spotlightCities: [
    {
      city: "Boise",
      state: "ID",
      hook:
        "Mountain capital where RNs keep more than two-thirds of every paycheck.",
      gross: 81200,
      taxes: 17890,
      leftover: 4115,
      crime: "A",
      hospitals: 6,
      summer: 91,
    },
    {
      city: "San Antonio",
      state: "TX",
      hook:
        "No state income tax and the deepest hospital network in central Texas.",
      gross: 78450,
      taxes: 14820,
      leftover: 4000,
      crime: "B",
      hospitals: 18,
      summer: 96,
    },
    {
      city: "Raleigh",
      state: "NC",
      hook:
        "Research-triangle salaries, mid-Atlantic weather, A-grade neighborhoods.",
      gross: 82400,
      taxes: 18130,
      leftover: 4020,
      crime: "B",
      hospitals: 9,
      summer: 88,
    },
    {
      city: "Tampa",
      state: "FL",
      hook: "Gulf-coast roster of 14 hospitals, no state income tax.",
      gross: 76900,
      taxes: 13450,
      leftover: 3870,
      crime: "C",
      hospitals: 14,
      summer: 90,
    },
    {
      city: "Madison",
      state: "WI",
      hook: "Big-ten medical center, lake-town crime grade.",
      gross: 79200,
      taxes: 17890,
      leftover: 3960,
      crime: "A",
      hospitals: 7,
      summer: 81,
    },
  ],

  hospitalsByCity: {
    "Sacramento, CA": [
      {
        name: "UC Davis Medical Center",
        lat: 38.5552,
        lng: -121.4544,
        salary: 124300,
      },
      {
        name: "Sutter Medical Center",
        lat: 38.5778,
        lng: -121.4694,
        salary: 118600,
      },
      {
        name: "Mercy General Hospital",
        lat: 38.5681,
        lng: -121.4575,
        salary: 116200,
      },
      {
        name: "Kaiser Permanente Sacramento",
        lat: 38.563,
        lng: -121.4282,
        salary: 121800,
      },
      {
        name: "Methodist Hospital of Sacramento",
        lat: 38.483,
        lng: -121.3835,
        salary: 114900,
      },
      {
        name: "VA Northern California",
        lat: 38.5559,
        lng: -121.2949,
        salary: 112400,
      },
      {
        name: "Dignity Health Mercy San Juan",
        lat: 38.6584,
        lng: -121.3477,
        salary: 117500,
      },
    ],
  },

  homeCity: { city: "Sacramento", state: "CA", openJobs: 47 },

  yourCities: [
    {
      city: "Boise",
      state: "ID",
      leftover: 4115,
      hospitals: 6,
      crime: "A",
      change: { kind: "jobs", value: 3 },
    },
    {
      city: "San Antonio",
      state: "TX",
      leftover: 4000,
      hospitals: 18,
      crime: "B",
      change: { kind: "salary", value: 2.1 },
    },
    {
      city: "Raleigh",
      state: "NC",
      leftover: 4020,
      hospitals: 9,
      crime: "B",
      change: { kind: "community", value: 1 },
    },
    {
      city: "Tampa",
      state: "FL",
      leftover: 3870,
      hospitals: 14,
      crime: "C",
      change: { kind: "salary", value: -0.8 },
    },
    {
      city: "Madison",
      state: "WI",
      leftover: 3960,
      hospitals: 7,
      crime: "A",
      change: { kind: "jobs", value: 5 },
    },
    {
      city: "Charlotte",
      state: "NC",
      leftover: 4180,
      hospitals: 12,
      crime: "B",
      change: { kind: "salary", value: 1.5 },
    },
    {
      city: "Phoenix",
      state: "AZ",
      leftover: 3950,
      hospitals: 22,
      crime: "C",
      change: { kind: "community", value: 3 },
    },
  ],

  jobs: [
    {
      title: "ICU Registered Nurse",
      tag: "New",
      employer: "St. Luke's Boise",
      location: "Boise, ID",
      posted: "2h ago",
      afterHousing: 4280,
    },
    {
      title: "Travel RN · Cardiology",
      tag: "Featured",
      employer: "Methodist Hospital",
      location: "San Antonio, TX",
      posted: "1d ago",
      afterHousing: 4640,
    },
    {
      title: "PACU Nurse",
      tag: "New",
      employer: "WakeMed Raleigh",
      location: "Raleigh, NC",
      posted: "5h ago",
      afterHousing: 4120,
    },
    {
      title: "ER Registered Nurse",
      tag: "Featured",
      employer: "Tampa General Hospital",
      location: "Tampa, FL",
      posted: "3h ago",
      afterHousing: 4380,
    },
    {
      title: "Labor & Delivery RN",
      tag: "New",
      employer: "UW Health Madison",
      location: "Madison, WI",
      posted: "8h ago",
      afterHousing: 4210,
    },
    {
      title: "Oncology RN",
      tag: "New",
      employer: "Atrium Health Charlotte",
      location: "Charlotte, NC",
      posted: "1d ago",
      afterHousing: 4450,
    },
  ],

  posts: [
    {
      name: "Maria L.",
      initials: "ML",
      when: "2h",
      tag: "Pay",
      body:
        "Just got my first travel contract for Sacramento. Actually clearing $7,200/mo after a $2,400 furnished sublet. Worth showing the breakdown?",
      likes: 124,
      comments: 34,
    },
    {
      name: "Devon K.",
      initials: "DK",
      when: "6h",
      tag: "Cities",
      body:
        "Anyone else seeing per-diem rates drop in Phoenix? Was $115/hr last spring, now $95. Comparing Boise vs. SLC for next contract.",
      likes: 87,
      comments: 0,
    },
  ],

  topByLeftover: [
    { city: "Boise", state: "ID", hospitals: 6, crime: "A", leftover: 4115 },
    { city: "Raleigh", state: "NC", hospitals: 9, crime: "B", leftover: 4020 },
    {
      city: "San Antonio",
      state: "TX",
      hospitals: 18,
      crime: "B",
      leftover: 4000,
    },
    { city: "Madison", state: "WI", hospitals: 7, crime: "A", leftover: 3960 },
    { city: "Tampa", state: "FL", hospitals: 14, crime: "C", leftover: 3870 },
  ],
};

/* ─── inline icons ──────────────────────────────────────────── */
const Icon = {
  bell: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  ),
  pin: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  hospital: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M3 21h18M5 21V7l7-4 7 4v14M10 12h4M12 10v4" />
    </svg>
  ),
  shield: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  sun: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  ),
  users: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  chevR: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  ),
  arrowR: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  ),
  info: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-5" />
      <path d="M12 8h.01" />
    </svg>
  ),
  plus: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      {...p}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  heart: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  bubble: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  home: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M3 11l9-7 9 7v9a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2z" />
    </svg>
  ),
  tax: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M19 5 5 19" />
      <circle cx="7.5" cy="7.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
    </svg>
  ),
  arrowSwap: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M5 8h14M5 8l4-4M5 8l4 4M19 16H5M19 16l-4-4M19 16l-4 4" />
    </svg>
  ),
  trendUp: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="m3 17 6-6 4 4 8-8" />
      <path d="M14 7h7v7" />
    </svg>
  ),
  trendDown: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="m3 7 6 6 4-4 8 8" />
      <path d="M14 17h7v-7" />
    </svg>
  ),
  briefcase: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
};

/* ─── small atoms ───────────────────────────────────────────── */
// True on real mouse/trackpad devices, false on touch — lets us give
// desktop a hover-in/hover-out tooltip while mobile keeps a tap-to-toggle
// (hover alone flickers open/closed on tap: mobile browsers synthesize a
// mouseenter+mouseleave pair before the click lands).
const isHoverCapable = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

// Tooltip box width — clamped further per-viewport in reposition() below.
const INFO_TIP_WIDTH = 220;
const INFO_TIP_MARGIN = 12;

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const hoverCapable = useRef(isHoverCapable()).current;

  // Anchors the popup to the icon's actual screen position (fixed, not
  // relative to the wrapper) and clamps it so it never spills past the
  // left/right edge of the viewport — the old centered-under-icon
  // placement clipped off-screen for icons near a narrow column's edge.
  const reposition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(INFO_TIP_WIDTH, vw - INFO_TIP_MARGIN * 2);
    const iconCenter = rect.left + rect.width / 2;
    const left = Math.max(
      INFO_TIP_MARGIN,
      Math.min(iconCenter - width / 2, vw - width - INFO_TIP_MARGIN),
    );
    const arrowLeft = Math.max(10, Math.min(iconCenter - left, width - 10));
    setPos({ left, bottom: window.innerHeight - rect.top + 8, width, arrowLeft });
  };

  useLayoutEffect(() => {
    if (!show) return undefined;
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [show]);

  return (
    <span
      ref={wrapRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 6,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={hoverCapable ? () => setShow(false) : undefined}
    >
      <Icon.info
        style={{ width: 14, height: 14, color: "var(--fg-3)", cursor: "pointer" }}
        aria-label="More info"
        onClick={(e) => {
          e.stopPropagation();
          setShow((s) => !s);
        }}
      />
      {show &&
        pos &&
        createPortal(
          // Portaled to document.body: the right sidebar column
          // (.mmp-twocol__right) is `position: sticky` at desktop widths,
          // which creates its own stacking context. A z-index set from
          // inside that context can never out-rank the app header's fixed
          // z-index (1040) — it only ranks locally within the sidebar's
          // context. Rendering here escapes that trap entirely.
          <div
            role="tooltip"
            style={{
              position: "fixed",
              left: pos.left,
              bottom: pos.bottom,
              // Literal color, not var(--brand-ink): that custom property
              // is scoped to .mmp-app, and this popup is portaled to
              // document.body (outside .mmp-app), so the var() would
              // resolve to nothing and background would fall back to
              // transparent.
              background: "#0e2a47",
              color: "#fff",
              fontSize: 12,
              fontWeight: 400,
              textTransform: "none",
              letterSpacing: "normal",
              lineHeight: 1.4,
              padding: "8px 10px",
              borderRadius: 8,
              width: pos.width,
              zIndex: 2000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            }}
          >
            {text}
            <div
              style={{
                position: "absolute",
                bottom: -4,
                left: pos.arrowLeft,
                transform: "translateX(-50%) rotate(45deg)",
                width: 8,
                height: 8,
                // Literal color, not var(--brand-ink): that custom property
                // is scoped to .mmp-app, and this popup is portaled to
                // document.body (outside .mmp-app), so the var() would
                // resolve to nothing and background would fall back to
                // transparent.
                background: "#0e2a47",
              }}
            />
          </div>,
          document.body,
        )}
    </span>
  );
};

const SectionTitle = ({ title, subtitle, action, info }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      marginBottom: 10,
    }}
  >
    <div>
      <h2
        style={{
          margin: 0,
          fontSize: 17,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: "var(--brand-ink)",
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {title}
        {info && <InfoTooltip text={info} />}
      </h2>
      {subtitle && (
        <div
          style={{
            fontSize: 12,
            color: "var(--fg-3)",
            marginTop: 4,
            lineHeight: 1,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
    {action}
  </div>
);

const Pill = ({ children, color = "neutral", icon }) => {
  const palette = {
    neutral: { bg: "var(--bg-muted)", fg: "var(--fg-2)" },
    success: { bg: "var(--success-bg)", fg: "var(--success)" },
    warning: { bg: "var(--warning-bg)", fg: "var(--warning)" },
    danger: { bg: "var(--danger-bg)", fg: "var(--danger)" },
    brand: { bg: "var(--brand-50)", fg: "var(--brand-700)" },
  }[color];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        height: 24,
        padding: "0 9px",
        background: palette.bg,
        color: palette.fg,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "-0.005em",
      }}
    >
      {icon}
      {children}
    </span>
  );
};

/* ════════════════════════════════════════════════════════════
   1 · CITY SPOTLIGHT
   ════════════════════════════════════════════════════════════ */
/* Skeleton placeholder shown until /city-spotlight resolves. Mirrors
   the real card's structure (eyebrow, title, 3 metric cards, pills,
   action row, dots) so the page doesn't jump when data arrives. */
function CitySpotlightSkeleton() {
  return (
    <div style={{ marginBottom: 12 }} aria-hidden="true">
      <div className="mmp-cs-sk">
        <div className="mmp-cs-sk__eyebrow">
          <span className="sk" style={{ width: 110, height: 10 }} />
          <span className="sk" style={{ width: 36, height: 10 }} />
        </div>
        <div className="mmp-cs-sk__title-row">
          <span className="sk" style={{ width: 180, height: 28 }} />
          <span
            className="sk"
            style={{ width: 32, height: 22, borderRadius: 999 }}
          />
          <span
            className="sk"
            style={{ width: 44, height: 22, borderRadius: 999 }}
          />
        </div>
        <div className="mmp-cs-sk__hook">
          <span
            className="sk"
            style={{ width: "92%", height: 12, marginBottom: 6 }}
          />
          <span className="sk" style={{ width: "70%", height: 12 }} />
        </div>
        <div className="mmp-cs-sk__metrics">
          {[0, 1, 2].map((i) => (
            <div key={i} className="mmp-cs-sk__metric">
              <span
                className="sk"
                style={{ width: "60%", height: 9, marginBottom: 8 }}
              />
              <span className="sk" style={{ width: "80%", height: 20 }} />
            </div>
          ))}
        </div>
        <div className="mmp-cs-sk__pills">
          <span
            className="sk"
            style={{ width: 78, height: 22, borderRadius: 999 }}
          />
          <span
            className="sk"
            style={{ width: 96, height: 22, borderRadius: 999 }}
          />
          <span
            className="sk"
            style={{ width: 88, height: 22, borderRadius: 999 }}
          />
        </div>
        <div className="mmp-cs-sk__actions">
          <span
            className="sk"
            style={{ flex: 1, height: 40, borderRadius: 10 }}
          />
          <span
            className="sk"
            style={{ flex: 1, height: 40, borderRadius: 10 }}
          />
          <span
            className="sk"
            style={{
              flex: "0 0 auto",
              width: 44,
              height: 40,
              borderRadius: 10,
            }}
          />
        </div>
        <div className="mmp-cs-sk__dots">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="sk"
              style={{
                width: i === 0 ? 18 : 6,
                height: 6,
                borderRadius: 999,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CitySpotlight({ savedCities, onSave, onViewDetails, refreshKey = 0 }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [idx, setIdx] = useState(0);
  // Distinguishes "loading" (cities:[] + hasLoaded:false → render null)
  // from "loaded but empty" (cities:[] + hasLoaded:true → render the
  // empty-state CTA card). Without this the empty state would flash
  // before the first fetch resolves.
  const [hasLoaded, setHasLoaded] = useState(false);
  // Direction of the last navigation: 1 → forward (slide in from the
  // right), -1 → backward (slide in from the left). Drives which slide
  // keyframe the cards play on each change.
  const [dir, setDir] = useState(1);
  // Index of the card we're sliding AWAY from. While set, it renders as
  // an absolutely-positioned overlay that animates off-screen so the old
  // and new cities slide together (a true carousel). Cleared on the
  // outgoing card's animationend. null = no transition in progress.
  const [outIdx, setOutIdx] = useState(null);
  // Swipe gesture origin. Stored in a ref so re-renders don't reset the
  // in-progress gesture between touchstart and touchend. Touch-only —
  // desktop intentionally has no swipe to keep mouse interactions
  // (button clicks) clean.
  const swipeStart = useRef(null);

  // Record the card we're leaving (outIdx) before advancing so it can
  // slide out as an overlay. Reads `idx` directly — the auto-advance
  // effect lists idx in its deps, so the interval always closes over a
  // fresh copy; no stale-closure guard needed.
  const goNext = () => {
    if (cities.length < 2) return;
    const cur = Math.min(idx, cities.length - 1);
    setDir(1);
    setOutIdx(cur);
    setIdx((cur + 1) % cities.length);
  };
  const goPrev = () => {
    if (cities.length < 2) return;
    const cur = Math.min(idx, cities.length - 1);
    setDir(-1);
    setOutIdx(cur);
    setIdx((cur - 1 + cities.length) % cities.length);
  };

  // Touch-only handlers — swipe is intentionally a mobile-only gesture.
  // On desktop the user navigates via the "Next city" button so mouse
  // drag would just get in the way (and previously broke the button
  // click via pointer capture).
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    swipeStart.current = { x: t.clientX, y: t.clientY };
  };
  const handleTouchEnd = (e) => {
    const start = swipeStart.current;
    if (!start) return;
    swipeStart.current = null;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // 40px threshold + horizontal-dominant gesture, so vertical page
    // scrolls aren't misread as swipes.
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };
  const handleTouchCancel = () => {
    swipeStart.current = null;
  };

  // Auto-advance to the next city every 15s. Restarts whenever `idx`
  // changes so a manual swipe / "Next city" click resets the timer
  // instead of triggering an immediate auto-advance right after.
  useEffect(() => {
    if (!cities.length || cities.length < 2) return undefined;
    const t = setInterval(goNext, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities.length, idx]);

  useEffect(() => {
    let cancelled = false;
    dispatch(getCitySpotlight())
      .then((result) => {
        if (cancelled) return;
        // /frontend/city-spotlight response shape:
        //   { data: { cities: [...] } }
        const rows = result?.payload?.data?.cities || [];
        if (!Array.isArray(rows) || rows.length === 0) return;
        // Annual leftover comes back from the API; the card displays a
        // monthly figure (matches the previous mock data shape) so we
        // divide by 12 here.
        const mapped = rows.map((r) => ({
          id: r.id,
          city: r.city,
          state: r.state,
          role: r.role,
          gross: Number(r.median_annual_salary || 0),
          taxes: Number(r.annual_taxes_paid || 0),
          leftover: Math.round(
            Number(r.left_over_annually_if_renting || 0) / 12,
          ),
          // Preserve null (don't fall back to "—"/0) so the card can hide
          // the crime / temperature tags entirely when the API has no value.
          crime: r.overall_crime_grade || null,
          hospitals: Number(r.hospital_count || 0),
          summer: r.summer_high == null ? null : Number(r.summer_high) || null,
          // Keep null/undefined as null (don't coerce to 0) so the card
          // can hide the population tag entirely when the API has no value.
          population:
            r.population == null ? null : Number(r.population) || null,
          isBookmarked: Boolean(r.is_bookmarked),
        }));
        setCities(mapped);
        // Don't reset idx on a refreshKey-driven refetch — keep the user
        // on the card they were just looking at so the heart flip stays
        // in view. Only reset on the initial mount load.
        setIdx((prev) => (prev >= mapped.length ? 0 : prev));
      })
      .catch((err) => {
        console.error("Failed to load city spotlight:", err);
      })
      .finally(() => {
        if (!cancelled) setHasLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // refreshKey is bumped by the parent after a bookmark toggle to
    // trigger a refetch + updated is_bookmarked flags on the cards.
  }, [dispatch, refreshKey]);

  // Before the first fetch settles, render a shimmering skeleton that
  // mirrors the card's layout so there's no jump when data arrives.
  if (!hasLoaded) return <CitySpotlightSkeleton />;

  // Loaded but no cities — render the empty-state CTA pointing the user
  // at Search so they can start saving cities.
  if (!cities || cities.length === 0) {
    return (
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            background: "var(--white)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: 24,
            textAlign: "center",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <div
            style={{
              fontSize: 13.5,
              lineHeight: 1.5,
              color: "var(--fg-2)",
              marginBottom: 16,
              maxWidth: 420,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            Save cities you&apos;re curious about and they&apos;ll appear here.
            Try our suggestions or browse 21,000+ cities in Search.
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/search")}
            style={{ minWidth: 180 }}
          >
            Browse cities in Search
          </button>
        </div>
      </div>
    );
  }
  const safeIdx = Math.min(idx, cities.length - 1);
  const c = cities[safeIdx];
  // Prefer the API's is_bookmarked when present, otherwise fall back to
  // the YourCities savedCities list (which may be more up to date for a
  // moment between the bookmark POST and the spotlight refetch).
  const isSaved =
    c.isBookmarked ||
    savedCities.some((s) => s.city === c.city && s.state === c.state);

  // Per-city body (title row → metrics → pills). Factored into a function
  // so the incoming AND outgoing cards can both render it during a slide
  // transition — the outgoing one is an absolute overlay that animates
  // off-screen while this (current) one slides in. Takes the city so the
  // crime palette is computed per-card, not just for the current one.
  const renderCityBody = (cc) => {
    // Bucket the palette by the base letter so A+/A-/B+/B-/C+/C-/D+/D-
    // each route to the same color as their plain letter. The old check
    // (`cc.crime <= "A"`) was string comparison and silently mis-routed
    // anything with a +/- suffix (e.g. "A+" sorts greater than "A").
    const crimeLetter = String(cc.crime || "")
      .charAt(0)
      .toUpperCase();
    const crimePalette =
      crimeLetter === "A" || crimeLetter === "B"
        ? "success"
        : crimeLetter === "C"
          ? "warning"
          : crimeLetter === "D" || crimeLetter === "F"
            ? "danger"
            : "warning"; // unknown / missing → neutral-ish
    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: "-0.025em",
              color: "var(--brand-ink)",
              lineHeight: 1,
            }}
          >
            {cc.city}
          </h1>
          <span
            className="badge badge-state"
            style={{ height: 24, fontSize: 12 }}
          >
            {cc.state}
          </span>
          {cc.role && (
            <span
              className="badge badge-role"
              style={{ height: 24, fontSize: 11 }}
              title={cc.role}
            >
              {shortRole(cc.role)}
            </span>
          )}
        </div>

        {cc.hook && (
          <p
            style={{
              margin: "0 0 16px",
              fontSize: 13.5,
              lineHeight: 1.5,
              color: "var(--fg-2)",
              maxWidth: "94%",
            }}
          >
            {cc.hook}
          </p>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
            marginBottom: 14,
          }}
        >
          {[
            {
              k: "Gross salary",
              v: `$${(cc.gross / 1000).toFixed(1)}k`,
              sub: "/yr",
              col: "var(--fg)",
            },
            {
              k: "Taxes",
              v: `$${(cc.taxes / 1000).toFixed(1)}k`,
              sub: "/yr",
              col: "var(--fg-2)",
            },
            {
              k: "Leftover",
              v: `$${cc.leftover.toLocaleString()}`,
              sub: "/mo",
              col: "var(--brand-600)",
            },
          ].map((s, i) => (
            <div
              key={i}
              className="mmp-cs__salary-card"
              style={{
                background: "var(--white)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "10px 12px",
                // Let the grid column actually shrink to 1fr rather
                // than expand to fit the value text — without this
                // the LEFTOVER card was overflowing the outer card's
                // right padding on narrow viewports.
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--fg-3)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {s.k}
              </div>
              <div
                className="t-num mmp-cs__salary-val"
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: s.col,
                  lineHeight: 1.1,
                  marginTop: 4,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {s.v}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: "var(--fg-3)",
                    letterSpacing: 0,
                    marginLeft: 2,
                  }}
                >
                  {s.sub}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          {cc.crime != null && (
            <Pill
              color={crimePalette}
              icon={<Icon.shield style={{ width: 12, height: 12 }} />}
            >
              Crime {cc.crime}
            </Pill>
          )}
          <Pill
            color="neutral"
            icon={<Icon.hospital style={{ width: 12, height: 12 }} />}
          >
            {cc.hospitals} hospitals
          </Pill>
          {cc.summer != null && (
            <Pill
              color={cc.summer >= 95 ? "warning" : "neutral"}
              icon={<Icon.sun style={{ width: 12, height: 12 }} />}
            >
              Summer {cc.summer}°F
            </Pill>
          )}
          {cc.population != null && (
            <Pill
              color="neutral"
              icon={<Icon.users style={{ width: 12, height: 12 }} />}
            >
              {formatCompactNumber(cc.population)} people
            </Pill>
          )}
        </div>
      </>
    );
  };

  // The card we're leaving (if a transition is in progress). Guarded so a
  // stale outIdx never points past the array after a refetch.
  const outCity =
    outIdx != null && outIdx !== safeIdx && outIdx < cities.length
      ? cities[outIdx]
      : null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        style={{
          position: "relative",
          background: "var(--white)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: 18,
          boxShadow: "var(--shadow-card)",
          overflow: "hidden",
          // Let vertical page scroll pass through on touch; we only
          // intercept horizontal swipes via the touch handlers above.
          // Desktop deliberately has no swipe — navigation is via the
          // "Next city" button.
          touchAction: "pan-y",
        }}
      >
        <svg
          viewBox="0 0 360 220"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            opacity: 0.55,
          }}
        >
          <defs>
            <linearGradient id="csg" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--brand-50)" />
              <stop offset="100%" stopColor="var(--white)" />
            </linearGradient>
          </defs>
          <rect width="360" height="220" fill="url(#csg)" />
          <g
            fill="none"
            stroke="var(--brand-200)"
            strokeWidth="1"
            opacity="0.55"
          >
            <path d="M-20,40 Q90,20 180,52 T380,32" />
            <path d="M-20,70 Q90,52 180,82 T380,64" />
            <path d="M-20,100 Q90,82 180,112 T380,98" />
          </g>
        </svg>

        <div style={{ position: "relative" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--brand-600)",
              }}
            >
              ◆ City spotlight
            </span>
            <span style={{ fontSize: 11, color: "var(--fg-4)" }}>
              {safeIdx + 1} / {cities.length}
            </span>
          </div>

          {/* Carousel viewport. The incoming (current) card sits in normal
              flow so it defines the height; the outgoing card is an absolute
              overlay on top. On a change both slide together — old exits to
              one edge while new enters from the other. outIdx is cleared on
              the outgoing card's animationend, leaving just the current card.
              Keyed by index so React remounts each card to re-fire its
              keyframe. The action row + dots below stay fixed. */}
          <div className="mmp-cs__viewport">
            <div
              key={`in-${safeIdx}`}
              className={`mmp-cs__slide mmp-cs__slide--in ${outCity
                ? dir >= 0
                  ? "is-next"
                  : "is-prev"
                : "is-static"
                }`}
            >
              {renderCityBody(c)}
            </div>
            {outCity && (
              <div
                key={`out-${outIdx}`}
                aria-hidden="true"
                className={`mmp-cs__slide mmp-cs__slide--out ${dir >= 0 ? "is-next" : "is-prev"
                  }`}
                onAnimationEnd={() => setOutIdx(null)}
              >
                {renderCityBody(outCity)}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{ flex: 1 }}
              data-tour="target-view-details"
              onClick={() => {
                if (!c.id) return;
                if (onViewDetails) {
                  onViewDetails(c);
                } else {
                  navigate(`/search/details/${c.id}`);
                }
              }}
              disabled={!c.id}
            >
              View details
            </button>
            <button
              className="btn btn-secondary"
              onClick={goNext}
              style={{ flex: 1 }}
              data-tour="target-next-city"
            >
              Next city
            </button>
            <button
              onClick={() => onSave && onSave(c)}
              aria-pressed={isSaved}
              aria-label={isSaved ? "Saved" : "Save city"}
              data-tour="target-save-city"
              style={{
                flex: "0 0 auto",
                width: 44,
                height: 40,
                borderRadius: 10,
                border: `1px solid ${isSaved ? "var(--brand-500)" : "var(--border)"
                  }`,
                background: isSaved ? "var(--brand-50)" : "var(--white)",
                color: isSaved ? "var(--brand-600)" : "var(--fg-3)",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                transition: "all 200ms var(--ease-out)",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill={isSaved ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 6,
              marginTop: 14,
            }}
          >
            {cities.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === idx ? 18 : 6,
                  height: 6,
                  borderRadius: 999,
                  background:
                    i === idx ? "var(--brand-500)" : "var(--border-strong)",
                  transition: "all 220ms var(--ease-out)",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   2 · YOUR CITIES (horizontal strip)
   ════════════════════════════════════════════════════════════ */
function YourCities({
  cities,
  total = 0,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  onRemove,
  onCardClick,
  scrollResetKey = 0,
}) {
  const scrollRef = useRef(null);
  const railRef = useRef(null);
  const navigate = useNavigate();
  // 3-slot rotating indicator. 0 → new jobs, 1 → salary change,
  // 2 → new community posts. Advances every 3s for all cards in sync.
  const [indicatorIdx, setIndicatorIdx] = useState(0);

  // Desktop infinite scroll — vertical scroll inside .mmp-yc__scroll
  // (height-capped at 245px). On mobile this element has display:contents
  // so it generates no box and never fires scroll events.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      if (!hasMore || loadingMore || !onLoadMore) return;
      const remaining = el.scrollHeight - (el.scrollTop + el.clientHeight);
      if (remaining < 80) onLoadMore();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, onLoadMore]);

  // Mobile infinite scroll — horizontal swipe on .mmp-yc__rail. On mobile
  // .mmp-yc__scroll has display:contents so only the rail is scrollable.
  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      if (!hasMore || loadingMore || !onLoadMore) return;
      const remaining = el.scrollWidth - (el.scrollLeft + el.clientWidth);
      if (remaining < 80) onLoadMore();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, onLoadMore]);

  // Reset the horizontal rail to the left edge whenever the parent
  // refreshes page 1 (scrollResetKey increments in refreshBookmarks).
  // This prevents the browser from restoring a stale right-end scroll
  // position that would immediately trigger onLoadMore and load all pages.
  useEffect(() => {
    if (railRef.current) {
      railRef.current.scrollLeft = 0;
    }
  }, [scrollResetKey]);

  // Rotate the indicator slot every 15 seconds. Only runs when there's
  // actually something to rotate to avoid a stray timer when the list
  // is empty.
  useEffect(() => {
    if (!cities || cities.length === 0) return undefined;
    const t = setInterval(() => {
      setIndicatorIdx((i) => (i + 1) % 3);
    }, 15000);
    return () => clearInterval(t);
  }, [cities]);

  const savedLabel = total > 0 ? total : cities.length;

  return (
    <div className="mmp-yc" style={{ marginBottom: 12 }}>
      <SectionTitle
        title="Your cities"
        subtitle={`${savedLabel} saved · synced across devices`}
        info="Cities you save from Search appear here, making it easy to compare, revisit, and manage them anytime.."
        action={
          <button
            type="button"
            data-tour="target-manage-cities"
            onClick={() =>
              navigate("/search", { state: { openBookmarks: true } })
            }
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              fontSize: 12,
              fontWeight: 600,
              color: "var(--brand-600)",
              cursor: "pointer",
            }}
          >
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
            >
              Manage
              <Icon.arrowR style={{ width: 13, height: 13 }} />
            </span>
          </button>
        }
      />
      <div
        ref={railRef}
        className="mmp-yc__rail"
        style={{
          display: "flex",
          gap: 10,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          padding: "4px 0 6px",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div className="mmp-yc__scroll" ref={scrollRef}>
          {cities.length === 0 && (
            <div
              style={{
                padding: "20px 16px",
                textAlign: "center",
                color: "var(--fg-3)",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--brand-ink)",
                  marginBottom: 4,
                }}
              >
                No cities saved yet
              </div>
              <div>
                Save cities from search cities to compare them here at a glance.
              </div>
            </div>
          )}
          {cities.map((c, cardIdx) => {
            // Build the list of indicators that actually have data for
            // this card. An indicator is omitted when its source value
            // is 0, null, or undefined. The card then rotates only
            // through what it has: 0 → no pill, 1 → static pill, 2+ →
            // 15s rotation driven by the global indicatorIdx.
            const sm = c.summary || {};
            const availableIndicators = [];

            const jobsCount = Number(sm.new_jobs_this_week || 0);
            if (jobsCount > 0) {
              availableIndicators.push({
                icon: <Icon.briefcase style={{ width: 11, height: 11 }} />,
                text: `${jobsCount} new job${jobsCount === 1 ? "" : "s"
                  } this week`,
                fg: "var(--brand-600)",
                bg: "var(--brand-50)",
              });
            }

            const salaryRaw = sm.salary_change_vs_current_city;
            const salaryVal =
              salaryRaw === null || salaryRaw === undefined
                ? null
                : Number(salaryRaw);
            if (salaryVal !== null && salaryVal !== 0) {
              if (salaryVal > 0) {
                availableIndicators.push({
                  icon: <Icon.trendUp style={{ width: 11, height: 11 }} />,
                  text: `Salary up ${salaryVal.toFixed(1)}%`,
                  fg: "var(--success)",
                  bg: "var(--success-bg)",
                });
              } else {
                availableIndicators.push({
                  icon: <Icon.trendDown style={{ width: 11, height: 11 }} />,
                  text: `Salary down ${Math.abs(salaryVal).toFixed(1)}%`,
                  fg: "var(--danger)",
                  bg: "var(--danger-bg)",
                });
              }
            }

            const postsCount = Number(sm.new_community_posts_this_week || 0);
            if (postsCount > 0) {
              availableIndicators.push({
                icon: <Icon.bubble style={{ width: 11, height: 11 }} />,
                text:
                  postsCount === 1
                    ? "New community post"
                    : `${postsCount} new community posts`,
                fg: "var(--fg-2)",
                bg: "var(--bg-muted)",
              });
            }

            const indicator =
              availableIndicators.length > 0
                ? availableIndicators[indicatorIdx % availableIndicators.length]
                : null;

            return (
              <div
                key={c.id ?? c.city + c.state}
                className="mmp-yc__card"
                // Only the first card carries the tour hook, so the
                // walkthrough has a single stable target to highlight.
                data-tour={cardIdx === 0 ? "target-city-card" : undefined}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!c.id) return;
                  if (onCardClick) {
                    onCardClick(c);
                  } else {
                    navigate(`/search/details/${c.id}`);
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && c.id) {
                    e.preventDefault();
                    if (onCardClick) {
                      onCardClick(c);
                    } else {
                      navigate(`/search/details/${c.id}`);
                    }
                  }
                }}
                style={{
                  position: "relative",
                  scrollSnapAlign: "start",
                  flex: "0 0 auto",
                  width: 168,
                  background: "var(--white)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  padding: 12,
                  cursor: c.id ? "pointer" : "default",
                }}
              >
                {/* Remove (un-bookmark) — stops propagation so the
                    card's own click handler doesn't navigate to the
                    detail page. Hidden when no handler is wired. */}
                {onRemove && (
                  <button
                    type="button"
                    className="mmp-yc__remove"
                    data-tour={
                      cardIdx === 0 ? "target-city-remove" : undefined
                    }
                    aria-label={`Remove ${c.city} from saved cities`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(c);
                    }}
                    onKeyDown={(e) => {
                      // Don't let Enter/Space bubble to the card and
                      // trigger navigation.
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                      }
                    }}
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 0,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: "1px solid var(--border)",
                      background: "var(--white)",
                      color: "var(--fg-3)",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      padding: 0,
                      lineHeight: 1,
                      zIndex: 1,
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                )}
                <div className="mmp-yc__main">
                  <div
                    className="mmp-yc__head"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      // Tight on mobile so "LEFTOVER" sits right under
                      // the city + state. Desktop overrides this via
                      // dashboard.scss (.mmp-yc__head { margin-bottom: 4 })
                      // inside the desktop media query.
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--brand-ink)",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {c.city}
                    </span>
                    <span
                      className="badge badge-state"
                      style={{ height: 18, fontSize: 10, padding: "0 5px" }}
                    >
                      {c.state}
                    </span>
                  </div>
                  <div className="mmp-yc__stats">
                    {c.hospitals} hospitals{" "}
                    <span className="mmp-yc__dot">·</span> Grade{" "}
                    {c.crime || "—"}
                  </div>
                  <div
                    className="mmp-yc__lo-label"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "var(--fg-3)",
                    }}
                  >
                    Leftover
                  </div>
                  <div
                    className="mmp-yc__lo-mobile t-num"
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "var(--brand-600)",
                      letterSpacing: "-0.02em",
                      marginTop: 2,
                    }}
                  >
                    ${c.leftover.toLocaleString()}
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: "var(--fg-3)",
                      }}
                    >
                      {" "}
                      /mo
                    </span>
                  </div>
                  {indicator && (
                    <div
                      /* Key on the rotation slot so React unmounts the
                         old pill and mounts a fresh one each rotation —
                         that re-triggers the swipe-in CSS animation. */
                      key={indicatorIdx}
                      className="mmp-yc__indicator"
                      style={{
                        marginTop: 8,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 7px",
                        borderRadius: 999,
                        background: indicator.bg,
                        color: indicator.fg,
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: "-0.005em",
                        maxWidth: "100%",
                      }}
                    >
                      {indicator.icon}
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {indicator.text}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mmp-yc__amount">
                  <div className="t-num mmp-yc__amount-val">
                    ${c.leftover.toLocaleString()}
                  </div>
                  <div className="mmp-yc__amount-suffix">/mo leftover</div>
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className="mmp-yc__add"
          onClick={() => navigate("/search")}
          style={{
            scrollSnapAlign: "start",
            flex: "0 0 auto",
            width: 168,
            border: "1.5px dashed var(--border-strong)",
            borderRadius: 12,
            padding: 12,
            background: "transparent",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            color: "var(--fg-3)",
            cursor: "pointer",
          }}
        >
          <Icon.plus style={{ width: 22, height: 22 }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Add city</span>
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   3 · HOMEOWNERSHIP SNAPSHOT
   ════════════════════════════════════════════════════════════ */
// PMT helper — standard amortization formula. Pulled out so both the
// scenario-driven and profile-fallback code paths share the same math.
const computeMortgagePMT = (principal, ratePct, termYears) => {
  const r = ratePct / 100 / 12;
  const n = termYears * 12;
  if (r === 0) return principal / n;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
};

// Build the card view-model from a single mortgage scenario row.
// We deliberately do NOT use the scenario's stored down_payment_pct or
// monthly_payment for the snapshot — those reflect whatever the user
// experimented with in the calculator (often 0% down). For the
// homeownership *snapshot* we want a consistent baseline that matches
// the profile-fallback formula: 20% down, scenario's interest rate +
// loan term, plus PITI add-ons.
const snapshotFromScenario = (s, netMonthlyFallback) => {
  const median = Number(s.home_price) || 0;
  // Force the same baseline as the profile path so PITI for a given
  // home price comes out identical no matter where the row came from:
  //   - 20% down (overrides scenario.down_payment_pct, often 0)
  //   - 6.85% APR (overrides scenario.interest_rate, often 7.065)
  //   - 30 yr term
  // Without these overrides a $816K home returns $5,264 in profile mode
  // but a different number when the same home arrives via the
  // /mortgage-scenarios endpoint.
  const downPmtPct = 20;
  const ratePct = 6.85;
  const termYears = 30;
  const downPmt = Math.round((median * downPmtPct) / 100);
  const principal = median - downPmt;
  const pi = computeMortgagePMT(principal, ratePct, termYears);
  const mortgage = Math.round(
    pi + (median * 0.011) / 12 + (median * 0.0035) / 12,
  );
  // Prefer the scenario's own net_annual_salary (the API now ships it
  // per row), fall back to the profile-derived monthly passed in by
  // the parent if the scenario doesn't carry one.
  const scenarioNetAnnual = Number(s.median_annual_salary);
  const netMonthly =
    scenarioNetAnnual > 0 ? scenarioNetAnnual / 12 : netMonthlyFallback;
  const pct = netMonthly > 0 ? Math.round((mortgage / netMonthly) * 100) : 0;
  return {
    id: s.id,
    city: s.city || "your city",
    median,
    downPmt,
    downPmtPct,
    mortgage,
    pct,
  };
};

// Fallback view-model from the auth user's profile (used when the
// scenarios API returns nothing). Preserves the previous behavior: 20%
// down, 6.85% APR, 30 yr term.
const snapshotFromProfile = (authUser, netMonthly) => {
  const city = authUser?.city ?? "Sacramento";
  const median = Number(authUser?.home_city_median_home_price) || 472000;
  const downPmtPct = 20;
  const downPmt = Math.round(median * 0.2);
  const principal = median * 0.8;
  const pi = computeMortgagePMT(principal, 6.85, 30);
  const mortgage = Math.round(
    pi + (median * 0.011) / 12 + (median * 0.0035) / 12,
  );
  const pct = netMonthly > 0 ? Math.round((mortgage / netMonthly) * 100) : 0;
  return { city, median, downPmt, downPmtPct, mortgage, pct };
};

const tonesByPct = (pct) =>
  pct < 28
    ? {
      fg: "var(--success)",
      track: "rgba(63,138,83,0.18)",
      stroke: "var(--success)",
      wash: "var(--success-wash)",
      border: "var(--success-border)",
    }
    : pct < 36
      ? {
        fg: "var(--warning)",
        track: "rgba(199,120,24,0.18)",
        stroke: "var(--warning)",
        wash: "var(--warning-wash)",
        border: "var(--warning-border)",
      }
      : {
        fg: "var(--danger)",
        track: "rgba(160,32,33,0.18)",
        stroke: "var(--danger)",
        wash: "var(--danger-wash)",
        border: "var(--danger-border)",
      };

function SnapshotCard({
  id,
  city,
  median,
  downPmt,
  downPmtPct,
  mortgage,
  pct,
  multi,
}) {
  const navigate = useNavigate();
  const tone = tonesByPct(pct);
  const R = 28;
  const C = 2 * Math.PI * R;
  const filled = (pct / 100) * C;
  return (
    <button
      style={{
        all: "unset",
        cursor: "pointer",
        display: "block",
        // In single-card mode the card fills the column. In multi mode
        // it becomes a fixed-width snap target so the rail can scroll
        // through all scenarios.
        width: multi ? "92%" : "100%",
        flex: multi ? "0 0 92%" : undefined,
        scrollSnapAlign: multi ? "start" : undefined,
        background: "var(--white)",
        border: `1px solid ${tone.border}`,
        borderRadius: 14,
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
        paddingRight: 10, // a little extra room on the right for the gradient fade
        boxShadow: "var(--shadow-card)",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 120,
          height: "100%",
          background: `linear-gradient(270deg, ${tone.wash} 0%, transparent 100%)`,
          opacity: 0.55,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          position: "relative",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 4,
              // minWidth:0 lets the eyebrow span shrink below its
              // intrinsic content width so ellipsis can take over.
              minWidth: 0,
            }}
          >
            <Icon.home
              style={{
                width: 13,
                height: 13,
                color: tone.fg,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 9.5,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: tone.fg,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                flex: 1,
              }}
              title={`Snapshot · ${city}`}
            >
              Snapshot · {city}
            </span>
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: "var(--brand-ink)",
              letterSpacing: "-0.015em",
              lineHeight: 1.2,
              // Clamp the headline to 2 lines so a long city name (e.g.
              // "Rough and Ready") wraps cleanly instead of pushing the
              // body copy down or overlapping the donut.
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}
            title={`Can you buy in ${city}?`}
          >
            Can you buy in {city}?
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--fg-3)",
              marginTop: 5,
              lineHeight: 1.45,
            }}
          >
            Median home{" "}
            <b className="t-num" style={{ color: "var(--fg-2)" }}>
              {formatCompactCurrency(median)}
            </b>{" "}
            · Down payment{" "}
            <b className="t-num" style={{ color: "var(--fg-2)" }}>
              {formatCompactCurrency(downPmt)}
            </b>{" "}
            at {downPmtPct}%. Estimated PITI{" "}
            <b className="t-num" style={{ color: "var(--fg-2)" }}>
              ${mortgage.toLocaleString()}/mo
            </b>
            .
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              marginTop: 8,
              fontSize: 11.5,
              fontWeight: 700,
              color: tone.fg,
            }}
            data-tour="target-mortgage-calc"
            onClick={() => navigate("/mortgage", { state: { scenarioId: id } })}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            Open mortgage calculator{" "}
            <Icon.chevR style={{ width: 11, height: 11 }} />
          </div>
        </div>

        <div
          style={{
            flex: "0 0 auto",
            width: 78,
            height: 78,
            position: "relative",
          }}
        >
          <svg
            width="78"
            height="78"
            viewBox="0 0 78 78"
            style={{ display: "block", transform: "rotate(-90deg)" }}
          >
            <circle
              cx="39"
              cy="39"
              r={R}
              fill="none"
              stroke={tone.track}
              strokeWidth="6"
            />
            <circle
              cx="39"
              cy="39"
              r={R}
              fill="none"
              stroke={tone.stroke}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${filled} ${C - filled}`}
            />
          </svg>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              lineHeight: 1,
            }}
          >
            <div
              className="t-num"
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: tone.fg,
                letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              {pct}%
            </div>
            <div
              style={{
                fontSize: 8,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--fg-3)",
                marginTop: 3,
              }}
            >
              of pay
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function HomeownershipSnapshot() {
  const authUser = useSelector((state) => state.auth?.user);
  const [scenarios, setScenarios] = useState([]);

  // Fetch saved mortgage scenarios on mount. If the API returns one or
  // more, each becomes its own card (horizontally scrollable when >1).
  // If the API returns an empty list (or errors), fall through to the
  // profile-based snapshot so the section never goes blank.
  useEffect(() => {
    let cancelled = false;
    mortgageScenarioService
      .list()
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.data?.scenarios || [];
        setScenarios(rows);
      })
      .catch((err) => {
        console.error("Failed to load mortgage scenarios:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // netMonthly always comes from the profile — scenarios don't carry
  // salary, only home/loan inputs.
  const netAnnual = Number(authUser?.home_city_median_annual_salary);
  const netMonthly = netAnnual / 12;

  const cards =
    scenarios.length > 0
      ? scenarios.map((s) => snapshotFromScenario(s, netMonthly))
      : [snapshotFromProfile(authUser, netMonthly)];

  return (
    <div style={{ marginBottom: 12 }}>
      <div className="mmp-ho__section-title">
        <SectionTitle
          title="Homeownership"
          subtitle="Mortgage vs. take-home pay"
          info="Your monthly leftover is based on your saved paycheck. If you haven't saved one yet, we estimate it using your profile's home city and nursing role.."
        />
      </div>
      {cards.length > 1 ? (
        <div
          style={{
            display: "flex",
            gap: 10,
            overflowX: "auto",
            overflowY: "hidden",
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            paddingBottom: 6,
          }}
        >
          {cards.map((c, i) => (
            <SnapshotCard key={i} {...c} multi />
          ))}
        </div>
      ) : (
        <SnapshotCard {...cards[0]} />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   4 · NEW JOBS
   ════════════════════════════════════════════════════════════ */
function NewJobs({ nearbyJobsCount = 0, nearbyCity = "" } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  // Distinguishes "loading" (jobs:[] + hasLoaded:false) from "loaded but
  // empty" (jobs:[] + hasLoaded:true). Without this the empty state
  // would flash before the first fetch settles.
  const [hasLoaded, setHasLoaded] = useState(false);
  // Mirrors SimpleJobCard's daily-job-view gate. Populated when
  // jobViewService.record() returns 403 / JOB_VIEW_LIMIT_REACHED so the
  // RestrictionModal opens instead of navigating to the detail page.
  const [jobViewLimitInfo, setJobViewLimitInfo] = useState(null);
  // Rotating badge index — each card cycles through its own `badges`
  // array, advancing every 15s (mirrors YourCities' indicator rotation).
  // A single global index keeps every card's badge in sync.
  const [badgeIdx, setBadgeIdx] = useState(0);
  const home = MMP_DATA.homeCity;
  const authUser = useSelector((state) => state.auth?.user);

  // Card-click handler. Records the view first so the server can enforce
  // the free-plan daily cap; a 403 opens the upgrade modal and skips
  // navigation. Any other failure soft-fails through to navigation so we
  // don't swallow the user's click on a transient network/auth blip.
  const handleJobCardClick = async (jobId) => {
    if (!jobId) return;
    try {
      await jobViewService.record(jobId);
    } catch (err) {
      const body = err?.response?.data;
      if (
        err?.response?.status === 403 &&
        body?.errorType === "JOB_VIEW_LIMIT_REACHED"
      ) {
        setJobViewLimitInfo({
          planSlug: body?.data?.plan_slug || "free",
          used: body?.data?.used ?? 5,
          limit: body?.data?.limit ?? 5,
        });
        return;
      }
    }
    // Tag the navigation with from=dashboard so the job-detail back arrow
    // returns to the dashboard instead of defaulting to the Jobs list.
    navigate(`/job-detail/${jobId}`, { state: { from: "dashboard" } });
  };

  // /frontend/personalized-jobs response shape:
  //   { data: { personalized: [...], popular_in_city: [...] } }
  // Each job is flat: id, company_name, location_city, location_state,
  // salary_min/max/period, posted_date, title, tag ("Matches your role"
  // or "Popular in your city" — already curated by the backend).
  // We merge personalized first (more relevant), then popular_in_city,
  // and cap the combined list at 6 for the card.
  useEffect(() => {
    let cancelled = false;
    const fetchJobs = async () => {
      try {
        const result = await dispatch(getPersonalizedJobs());
        if (cancelled) return;
        const data = result?.payload?.data || {};
        const personalized = Array.isArray(data.personalized)
          ? data.personalized
          : [];
        const popular = Array.isArray(data.popular_in_city)
          ? data.popular_in_city
          : [];
        // Render at most 3 jobs: guarantee at least 1 city slot when
        // popular_in_city has results so the user's city is always represented.
        const pSlots = popular.length > 0 ? 2 : 3;
        const rows = [...personalized.slice(0, pSlots), ...popular.slice(0, 3 - pSlots)];
        const mapped = rows.map((j) => {
          const city = j.location_city || "";
          const state = j.location_state || "";
          const locationStr =
            city && state ? `${city}, ${state}` : city || state || "";
          const salaryMin = Number(j.salary_min ?? 0);
          const salaryMax = Number(j.salary_max ?? 0);
          const salaryPeriod = j.salary_period || "";
          // Monthly take-home after rent — surfaced as the headline pay
          // figure on the card (replaces the gross salary range). Kept as
          // null when the API omits affordability so the card can fall
          // back to the salary range.
          const netAfterRentRaw = j.affordability?.net_monthly_after_rent;
          const netMonthlyAfterRent =
            netAfterRentRaw === null || netAfterRentRaw === undefined
              ? null
              : Number(netAfterRentRaw);
          // Posted timestamp → relative "X ago" string. Uses the same
          // human-readable format as the job cards elsewhere in the app
          // (min / hour / day / week / month / year) so e.g. 38 days reads
          // as "1 month ago" instead of "38d ago".
          const when = j.posted_date || j.created_at;
          let posted = "";
          if (when) {
            const diffMs = Date.now() - new Date(when).getTime();
            const seconds = Math.floor(diffMs / 1000);
            const minutes = Math.floor(seconds / 60);
            const hours = Math.floor(minutes / 60);
            const days = Math.floor(hours / 24);
            const months = Math.floor(days / 30);
            const years = Math.floor(days / 365);

            if (seconds < 60) posted = "Just now";
            else if (minutes < 60)
              posted = minutes === 1 ? "1 min ago" : `${minutes} min ago`;
            else if (hours < 24)
              posted = hours === 1 ? "1 hour ago" : `${hours} hours ago`;
            else if (days >= 7 && days <= 28 && days % 7 === 0) {
              const weeks = days / 7;
              posted = weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
            } else if (days < 30)
              posted = days === 1 ? "1 day ago" : `${days} days ago`;
            else if (months < 12)
              posted = months === 1 ? "1 month ago" : `${months} months ago`;
            else posted = years === 1 ? "1 year ago" : `${years} years ago`;
          }
          return {
            id: j.id,
            title: j.title || "Untitled role",
            // Use the API-provided tag directly ("Matches your role" /
            // "Popular in your city"). Backend handles classification —
            // no more client-side 24h "New" rule.
            tag: j.tag || null,
            // Rotating badges (e.g. "Trending", "Hospital"). The card
            // cycles through these every 15s; empty array → no badge.
            badges: Array.isArray(j.badges) ? j.badges.filter(Boolean) : [],
            employer: j.company_name || "—",
            location: locationStr,
            posted,
            salaryMin: Number.isFinite(salaryMin) ? salaryMin : 0,
            salaryMax: Number.isFinite(salaryMax) ? salaryMax : 0,
            salaryPeriod,
            netMonthlyAfterRent:
              netMonthlyAfterRent !== null &&
                Number.isFinite(netMonthlyAfterRent)
                ? netMonthlyAfterRent
                : null,
          };
        });
        setJobs(mapped);
      } catch (err) {
        console.error("Failed to load jobs:", err);
      } finally {
        if (!cancelled) setHasLoaded(true);
      }
    };
    fetchJobs();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Advance the rotating badge index every 15s — same cadence as the
  // YourCities indicator. Only runs once there are jobs to rotate.
  useEffect(() => {
    if (!jobs || jobs.length === 0) return undefined;
    const t = setInterval(() => {
      setBadgeIdx((i) => i + 1);
    }, 15000);
    return () => clearInterval(t);
  }, [jobs]);

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--brand-ink)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              lineHeight: 1,
            }}
          >
            New {shortRole(authUser?.nursing_role?.role_title)} Jobs
            <InfoTooltip text="Jobs matching your nursing role and selected city. Listings are updated regularly as new opportunities become available." />
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                height: 22,
                padding: "0 9px",
                background: "var(--brand-50)",
                color: "var(--brand-700)",
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: "-0.005em",
              }}
            >
              <span className="t-num">{nearbyJobsCount}</span>
              <span style={{ fontWeight: 500, color: "var(--brand-600)" }}>
                open in {nearbyCity || home.city}
              </span>
            </span>
          </h2>
          <div
            style={{
              fontSize: 12,
              color: "var(--fg-3)",
              marginTop: 4,
              lineHeight: 1,
            }}
          >
            Based on your nursing role.
          </div>
        </div>
        <button
          onClick={() => navigate("/jobs")}
          data-tour="target-jobs-seeall"
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--brand-600)",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = "underline";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = "none";
          }}
        >
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
          >
            See all
            <Icon.arrowR style={{ width: 13, height: 13 }} />
          </span>
        </button>
      </div>
      <div
        className="mmp-jobs__list"
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        {hasLoaded && jobs.length === 0 && (
          <div
            className="mmp-jobs__empty"
            style={{
              background: "var(--white)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: "20px 18px",
              textAlign: "center",
              // Take the full available width — overrides the desktop
              // jobs rail's row layout (which would otherwise let the
              // empty card shrink to its content).
              flex: "1 1 auto",
              width: "100%",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "var(--brand-ink)",
                marginBottom: 6,
                lineHeight: 1.3,
              }}
            >
              No new jobs in your role yet
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--fg-3)",
                lineHeight: 1.4,
                marginBottom: 14,
              }}
            >
              {`We'll notify you when ${shortRole(
                authUser?.nursing_role?.role_title,
              ) || "your"} positions open in ${authUser?.city || "your city"}`}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
              }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate("/jobs")}
                style={{ minWidth: 160 }}
              >
                Browse all jobs
              </button>
              <button
                type="button"
                onClick={() => navigate("/profile?modal=jobs-notification")}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--brand-600)",
                }}
              >
                Get job alerts
              </button>
            </div>
          </div>
        )}
        {jobs.map((j, i) => (
          <div
            key={j.id ?? i}
            className="mmp-jobs__card"
            // Only the first job card carries the tour hook, so the
            // walkthrough has a single stable target to highlight.
            data-tour={i === 0 ? "target-job-card" : undefined}
            role={j.id ? "button" : undefined}
            tabIndex={j.id ? 0 : undefined}
            onClick={() => {
              if (j.id) handleJobCardClick(j.id);
            }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && j.id) {
                e.preventDefault();
                handleJobCardClick(j.id);
              }
            }}
            style={{
              // 2×2 CSS Grid (same in both mobile and desktop):
              //   title  (col 1, row 1)   |   tag    (col 2, row 1)
              //   company(col 1, row 2)   |   value  (col 2, row 2)
              // Each cell's placement is set inline below.
              display: "grid",
              gridTemplateColumns: "1fr auto",
              // Top-align every cell within its row so the title→company
              // gap on the left matches the tag→value gap on the right.
              alignItems: "start",
              columnGap: 12,
              rowGap: 4,
              background: "var(--white)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: "14px 16px",
              cursor: j.id ? "pointer" : "default",
            }}
          >
            {/* Title — top-left cell (col 1, row 1) */}
            <div
              className="mmp-jobs__title"
              style={{
                gridColumn: 1,
                gridRow: 1,
                minWidth: 0,
                overflowWrap: "break-word",
                fontSize: 14,
                fontWeight: 700,
                color: "var(--brand-ink)",
                letterSpacing: "-0.005em",
                // Match the badge height (18px) so the title text and the
                // tag pill sit on the same line and read as aligned.
                lineHeight: "18px",
              }}
            >
              {j.title}
            </div>
            {/* Tag — top-right cell (col 2, row 1) */}
            {j.badges.length > 0 &&
              (() => {
                // Cycle through this card's badges; the global badgeIdx
                // advances every 15s. Keying the <span> on badgeIdx
                // unmounts/remounts it each rotation so the swipe-in
                // animation replays — same trick as YourCities.
                const badge = j.badges[badgeIdx % j.badges.length];
                return (
                  <div
                    className="mmp-jobs__tag-wrap"
                    style={{
                      gridColumn: 2,
                      gridRow: 1,
                      justifySelf: "end",
                      alignSelf: "start",
                      // Flex (not a plain block) so the inline badge has no
                      // baseline leading above it — keeps the 18px pill
                      // flush with the title's 18px line box.
                      display: "flex",
                      alignItems: "center",
                      height: 18,
                    }}
                  >
                    <span
                      className={`badge ${
                        // Map the two backend categories to distinct palettes
                        // so users can scan personalized vs popular at a glance.
                        j.tag === "Popular in your city"
                          ? "badge-warning"
                          : "badge-success"
                        }`}
                      key={badgeIdx}
                      className={`badge mmp-jobs__badge ${jobBadgeClass(
                        badge,
                      )}`}
                      style={{ height: 18, fontSize: 10 }}
                    >
                      {badge}
                    </span>
                  </div>
                );
              })()}
            {/* Employer + location — bottom-left cell (col 1, row 2) */}
            <div
              className="mmp-jobs__info"
              style={{ gridColumn: 1, gridRow: 2, minWidth: 0 }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "var(--fg-2)",
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                {j.employer}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--fg-3)",
                  lineHeight: 1.3,
                  marginTop: 1,
                  display: "flex",
                  gap: 8,
                }}
              >
                <span>{j.location}</span>
                <span>·</span>
                <span>{j.posted}</span>
              </div>
            </div>
            {/* Value — bottom-right cell (col 2, row 2) */}
            <div
              className="mmp-jobs__pay"
              style={{
                gridColumn: 2,
                gridRow: 2,
                justifySelf: "end",
                textAlign: "right",
              }}
            >
              {j.netMonthlyAfterRent !== null ? (
                <>
                  <div
                    className="t-num mmp-jobs__salary"
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      color:
                        j.netMonthlyAfterRent < 0
                          ? "var(--danger)"
                          : "var(--brand-600)",
                      lineHeight: 1,
                    }}
                  >
                    {j.netMonthlyAfterRent < 0 ? "-" : ""}$
                    {Math.abs(j.netMonthlyAfterRent).toLocaleString()}
                  </div>
                  <div
                    className="mmp-jobs__salary-period"
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "var(--fg-3)",
                      marginTop: 1,
                    }}
                  >
                    After housing /mo
                  </div>
                </>
              ) : (
                <div
                  className="t-num mmp-jobs__salary"
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    color: "var(--brand-600)",
                    lineHeight: 1,
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <span>${j.salaryMin.toLocaleString()}</span>
                  <span> - </span>
                  <span>${j.salaryMax.toLocaleString()}</span>
                  {j.salaryPeriod && (
                    <span
                      className="mmp-jobs__salary-period"
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--fg-3)",
                        marginLeft: 2,
                      }}
                    >
                      /{j.salaryPeriod}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <RestrictionModal
        isVisible={Boolean(jobViewLimitInfo)}
        onClose={() => setJobViewLimitInfo(null)}
        eventSource="job_view_limit"
        iconType="briefcase"
        heading="You've reached your daily job-view limit"
        subText={`You've viewed your ${jobViewLimitInfo?.limit ??
          5} free jobs for today. Upgrade to Pro for unlimited job views.`}
        infoText={`You're on the ${(jobViewLimitInfo?.planSlug || "free")
          .charAt(0)
          .toUpperCase() +
          (jobViewLimitInfo?.planSlug || "free").slice(
            1,
          )} plan · ${jobViewLimitInfo?.used ??
          0} of ${jobViewLimitInfo?.limit ?? 5} job views used today`}
        thirdCtaMode="newsletter"
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   5 · HOSPITAL MAP (compact preview)
   ════════════════════════════════════════════════════════════ */
function HospitalMap() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileStore = useSelector((s) => s.profile);
  const comparisonStore = useSelector((s) => s.comparison);
  const authUser = useSelector((s) => s.auth?.user);

  const profile = profileStore?.profileItem?.data;
  // The profile store often loads after this component mounts (and on a
  // fresh session may not be populated at all), which left `state` blank
  // and the hospital-info request fired with state= empty. Fall back to
  // the auth user — those fields are already reliable on first render
  // (the header uses them to show "{city}, {living_state}").
  const city = profile?.city || authUser?.city || "";
  const state = profile?.living_state || authUser?.living_state || "";
  // Source the role id from the same paths the rest of the dashboard uses
  // (TopCities reads authUser?.nursing_role?.id). No hard-coded RN
  // fallback — if the profile genuinely has no role pinned, we want the
  // effect below to skip the request rather than silently fire as RN.
  const role =
    profile?.nursing_role?.id ||
    profile?.role ||
    authUser?.nursing_role?.id ||
    authUser?.role ||
    null;

  const hospitals = comparisonStore?.hospitalItems?.data?.hospitals || [];

  useEffect(() => {
    // Skip the request if any of role/city/state is missing — sending an
    // empty `state=` / `city=` (or no role) just churns the network and
    // returns nothing useful. The effect re-fires once profile/auth
    // resolves with real values.
    if (!role || !state || !city) return;
    dispatch(getHospitalData({ state, city, role }));
  }, [dispatch, role, state, city]);

  const cityShort = city || hospitals[0]?.city || "Sacramento";
  const stateShort = state || hospitals[0]?.state || "CA";

  return (
    <div style={{ marginBottom: 12 }}>
      <SectionTitle
        title="Hospital map"
        subtitle={`${hospitals.length} hospitals in ${cityShort}, ${stateShort}`}
      />
      <div
        style={{
          display: "block",
          width: "100%",
          background: "var(--white)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          overflow: "hidden",
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        <div
          className="mmp-mapwrap"
          style={{ position: "relative", height: 260 }}
        >
          <Map
            city={city}
            state={state}
            hospitalData={hospitals}
            restrictedData={{}}
          />

          <div
            style={{
              position: "absolute",
              left: 10,
              bottom: 10,
              zIndex: 500,
              background: "var(--white)",
              border: "1px solid var(--border)",
              borderRadius: 999,
              padding: "5px 10px",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--fg-2)",
              boxShadow: "var(--shadow-sm)",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              pointerEvents: "none",
            }}
          >
            <Icon.hospital
              style={{ width: 11, height: 11, color: "var(--brand-600)" }}
            />
            {hospitals.length} hospitals
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/full-map")}
          data-tour="target-fullmap"
          style={{
            all: "unset",
            cursor: "pointer",
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            borderTop: "1px solid var(--divider)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            <Icon.pin
              style={{
                width: 16,
                height: 16,
                color: "var(--brand-500)",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--brand-ink)",
                letterSpacing: "-0.005em",
              }}
            >
              {cityShort}, {stateShort}
            </span>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              fontWeight: 700,
              color: "var(--brand-600)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            View Full Map <Icon.chevR style={{ width: 12, height: 12 }} />
          </span>
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   6 · COMMUNITY
   ════════════════════════════════════════════════════════════ */
function CommunitySection() {
  // Page size matches the bookmarks pagination (BOOKMARK_PAGE_SIZE = 6).
  const POSTS_PAGE_SIZE = 2;
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const scrollRef = useRef(null);
  const { isPaid: isProUser } = usePlan();
  const [showSaveRestriction, setShowSaveRestriction] = useState(false);
  // Comment modal — when set, opens a Modal hosting <CommentsSection>
  // for that post. Mirrors the in-page comments UI from postDetails.js
  // without navigating away from the dashboard.
  const [commentModalPostId, setCommentModalPostId] = useState(null);

  const openCommentModal = (postId) => {
    if (!postId) return;
    setCommentModalPostId(postId);
  };
  const closeCommentModal = () => setCommentModalPostId(null);

  // Live-update the comment count on the matching card when a comment
  // is added or removed inside the modal.
  const handleCommentCountUpdate = (newCount) => {
    if (!commentModalPostId) return;
    setPosts((prev) =>
      prev.map((p) =>
        p.id === commentModalPostId
          ? { ...p, comments: Number(newCount) || 0 }
          : p,
      ),
    );
  };
  // See loadingMoreBookmarksRef in DashboardHome — scroll events fire
  // faster than React re-renders, so a state-based guard reads stale
  // closure values and lets duplicate fetches through (page 2 → 2 calls,
  // page 3 → 3 calls). A ref updates synchronously and blocks them.
  const loadingMoreRef = useRef(false);

  // Convert an adapted post (mapPostsFromAPI output) into the shape the
  // existing card JSX below expects (initials/name/when/tag/body/likes/
  // comments). Keeps the markup untouched.
  const toCard = (p) => {
    const name = p.author || "Anonymous";
    const initials =
      name
        .split(/\s+/)
        .map((w) => w.charAt(0))
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "??";
    // formatTimeAgo already returns strings like "2h ago" / "Just now";
    // the JSX appends " ago", so strip the trailing word if present.
    const when = String(p.time || "").replace(/\s*ago\s*$/i, "");
    // Author avatar path — built the same way PostCard.js does it:
    // the API returns just a filename (e.g. "12345_photo.jpg"), and the
    // image lives under `uploads/user_profile/` on the API host. If the
    // path already starts with that prefix or is a full URL, leave it.
    let authorImage = null;
    if (p.authorImage && p.authorImage !== name) {
      const raw = p.authorImage;
      if (raw.startsWith("http://") || raw.startsWith("https://")) {
        authorImage = raw;
      } else {
        const base = process.env.REACT_APP_API_BASE_URL || "";
        const path = raw.startsWith("uploads/user_profile/")
          ? raw
          : `uploads/user_profile/${raw}`;
        authorImage = `${base}/${path}`;
      }
    }
    return {
      id: p.id,
      name,
      initials,
      authorImage,
      when,
      tag: p.categoryTag || p.category || "General",
      // Render title as the prominent line, content as the subtitle below.
      title: p.title || "",
      content: p.content || "",
      // Adapter stores the first post image's relative path on `image`;
      // wrap it through getFullImageUrl so we hit the API host.
      image: p.image ? getFullImageUrl(p.image) : null,
      likes: Number(p.likes ?? 0),
      comments: Number(p.comments ?? 0),
      // Reaction fields from the adapter — drive the thumbs-up toggle
      // and the optimistic count update on click.
      userReaction: p.userReaction || null,
      reactions: p.reactions || {
        like: 0,
        love: 0,
        haha: 0,
        wow: 0,
        sad: 0,
        angry: 0,
      },
      // Save / bookmark state — seeded from the API's is_saved flag.
      isSaved: Boolean(p.isSaved),
      saveLoading: false,
    };
  };

  // Save / unsave a post — mirrors PostCard's handleSaveClick. Calls
  // /frontend/post-saves/toggle through communityService and rolls back
  // the optimistic flip on failure.
  const handleToggleSave = async (postId) => {
    if (!postId) return;
    const target = posts.find((x) => x.id === postId);
    if (!target || target.saveLoading) return;
    const previous = Boolean(target.isSaved);
    const next = !previous;

    // Free users cannot save posts — show the upgrade modal.
    // Un-saving an already-saved post is still allowed for everyone.
    if (!isProUser && !previous) {
      setShowSaveRestriction(true);
      return;
    }
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, isSaved: next, saveLoading: true } : p,
      ),
    );
    try {
      const response = await communityService.togglePostSave(postId);
      if (response?.data?.success) {
        toast.success(
          response?.data?.message ||
          (next ? "Post saved" : "Removed from saved"),
        );
      } else {
        setPosts((prev) =>
          prev.map((p) => (p.id === postId ? { ...p, isSaved: previous } : p)),
        );
        toast.error(response?.data?.message || "Failed to update save");
      }
    } catch (err) {
      console.error("Error toggling post save:", err);
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, isSaved: previous } : p)),
      );
      toast.error(err?.response?.data?.message || "Failed to update save");
    } finally {
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, saveLoading: false } : p)),
      );
    }
  };

  // Optimistic reaction handler — mirrors PostCard's onReaction flow
  // (Community.js → usePosts.addOrUpdateReaction). Clicking the same
  // reaction removes it; otherwise it sets the user's reaction to the
  // chosen one. Total likes = current reaction counts + legacy mobile
  // likes baseline (HYBRID approach).
  const handleSetReaction = async (postId, reactionType) => {
    if (!postId || !reactionType) return;
    let rollback = null;
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== postId) return p;
        rollback = {
          userReaction: p.userReaction,
          reactions: { ...p.reactions },
          likes: p.likes,
        };
        const currentTotal = Object.values(p.reactions || {}).reduce(
          (a, b) => a + (Number(b) || 0),
          0,
        );
        const legacyBaseline = Math.max(0, (p.likes || 0) - currentTotal);
        const nextReactions = { ...p.reactions };
        if (p.userReaction === reactionType) {
          nextReactions[reactionType] = Math.max(
            0,
            (nextReactions[reactionType] || 0) - 1,
          );
          const nextTotal = Object.values(nextReactions).reduce(
            (a, b) => a + (Number(b) || 0),
            0,
          );
          return {
            ...p,
            userReaction: null,
            reactions: nextReactions,
            likes: nextTotal + legacyBaseline,
          };
        }
        if (p.userReaction && p.userReaction !== reactionType) {
          nextReactions[p.userReaction] = Math.max(
            0,
            (nextReactions[p.userReaction] || 0) - 1,
          );
        }
        nextReactions[reactionType] = (nextReactions[reactionType] || 0) + 1;
        const nextTotal = Object.values(nextReactions).reduce(
          (a, b) => a + (Number(b) || 0),
          0,
        );
        return {
          ...p,
          userReaction: reactionType,
          reactions: nextReactions,
          likes: nextTotal + legacyBaseline,
        };
      }),
    );
    try {
      await communityService.addOrUpdateReaction(postId, reactionType);
    } catch (err) {
      console.error("Failed to update reaction:", err);
      if (rollback) {
        setPosts((prev) =>
          prev.map((p) => (p.id === postId ? { ...p, ...rollback } : p)),
        );
      }
    }
  };

  // Reaction picker hover state — keyed by post id so each card has
  // its own picker. Uses a small close-delay (matches PostCard) so the
  // user can move the mouse from the button onto the floating picker
  // without it disappearing.
  const [pickerOpenFor, setPickerOpenFor] = useState(null);
  const pickerHideTimerRef = useRef(null);

  const openPicker = (postId) => {
    if (pickerHideTimerRef.current) {
      clearTimeout(pickerHideTimerRef.current);
      pickerHideTimerRef.current = null;
    }
    setPickerOpenFor(postId);
  };
  const schedulePickerClose = () => {
    if (pickerHideTimerRef.current) clearTimeout(pickerHideTimerRef.current);
    pickerHideTimerRef.current = setTimeout(() => {
      setPickerOpenFor(null);
      pickerHideTimerRef.current = null;
    }, 250);
  };

  // Same icon map PostCard uses — emoji image for love/haha/wow/sad/
  // angry, ThumbsUp for like, with the active fill controlled below.
  const reactionIconFor = (type, active) => {
    if (type === "love")
      return <img src={loveImg} alt="love" width={16} height={14} />;
    if (type === "haha")
      return <img src={hahaImg} alt="haha" width={16} height={14} />;
    if (type === "wow")
      return <img src={wowImg} alt="wow" width={16} height={14} />;
    if (type === "sad")
      return <img src={sadImg} alt="sad" width={16} height={14} />;
    if (type === "angry")
      return <img src={angryImg} alt="angry" width={16} height={14} />;
    return <ThumbsUp size={13} fill={active ? "currentColor" : "none"} />;
  };
  const reactionLabel = (type) => {
    if (!type) return "Like";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Initial fetch — first page of 6. The getAllPosts response nests rows
  // at response.data.data.posts.{ data, total } (see usePosts hook).
  useEffect(() => {
    let cancelled = false;
    const fetchInitial = async () => {
      try {
        const response = await communityService.getAllPosts({
          page: 1,
          length: POSTS_PAGE_SIZE,
        });
        if (cancelled) return;
        const payload = response?.data?.data?.posts;
        const rows = mapPostsFromAPI(payload?.data || []).filter(
          (p) => p && p.id,
        );
        setPosts(rows.map(toCard));
        setTotal(Number(payload?.total ?? rows.length));
        setPage(1);
      } catch (err) {
        console.error("Failed to load community posts:", err);
      }
    };
    fetchInitial();
    return () => {
      cancelled = true;
    };
  }, []);

  // Infinite scroll — DISABLED. Scrolling the posts container no longer
  // pages in more posts; only the first page (POSTS_PAGE_SIZE) is shown.
  // Uncomment to re-enable scroll-based pagination.
  // useEffect(() => {
  //   const el = scrollRef.current;
  //   if (!el) return undefined;
  //   const onScroll = () => {
  //     if (loadingMoreRef.current) return;
  //     if (posts.length >= total) return;
  //     const remaining = el.scrollHeight - (el.scrollTop + el.clientHeight);
  //     if (remaining > 80) return;
  //     const loadMore = async () => {
  //       loadingMoreRef.current = true;
  //       try {
  //         const nextPage = page + 1;
  //         const response = await communityService.getAllPosts({
  //           page: nextPage,
  //           length: POSTS_PAGE_SIZE,
  //         });
  //         const payload = response?.data?.data?.posts;
  //         const rows = mapPostsFromAPI(payload?.data || []).filter(
  //           (p) => p && p.id,
  //         );
  //         if (rows.length === 0) return;
  //         setPosts((prev) => {
  //           const seen = new Set(prev.map((x) => x.id));
  //           return [
  //             ...prev,
  //             ...rows.map(toCard).filter((x) => !seen.has(x.id)),
  //           ];
  //         });
  //         setPage(nextPage);
  //       } catch (err) {
  //         console.error("Failed to load more community posts:", err);
  //       } finally {
  //         loadingMoreRef.current = false;
  //       }
  //     };
  //     loadMore();
  //   };
  //   el.addEventListener("scroll", onScroll, { passive: true });
  //   return () => el.removeEventListener("scroll", onScroll);
  // }, [page, posts.length, total]);

  return (
    <div style={{ marginBottom: 12 }}>
      <SectionTitle
        title="Community"
        subtitle="From the nurse forum"
        action={
          <button
            onClick={() => navigate("/community")}
            data-tour="target-community-feed"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--brand-600)",
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
            >
              Open feed
              <Icon.arrowR style={{ width: 13, height: 13 }} />
            </span>
          </button>
        }
      />
      <div
        ref={scrollRef}
        className="mmp-comm__list"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {posts.map((p, i) => (
          <div
            key={p.id ?? i}
            // Only the first post carries the tour hook, so the
            // walkthrough has a single stable target to highlight.
            data-tour={i === 0 ? "target-community-card" : undefined}
            role={p.id ? "button" : undefined}
            tabIndex={p.id ? 0 : undefined}
            onClick={() => {
              if (p.id) navigate(`/community/${p.id}`);
            }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && p.id) {
                e.preventDefault();
                navigate(`/community/${p.id}`);
              }
            }}
            style={{
              background: "var(--white)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: 14,
              cursor: p.id ? "pointer" : "default",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 8,
              }}
            >
              {p.authorImage ? (
                <img
                  src={p.authorImage}
                  alt={p.name || "User"}
                  width={28}
                  height={28}
                  className="avatar avatar-sm"
                  style={{
                    objectFit: "cover",
                    borderRadius: "50%",
                    flexShrink: 0,
                  }}
                  onError={(e) => {
                    // Fall back to a ui-avatars initials image (same
                    // graceful degradation PostCard uses) instead of
                    // showing a broken-image icon.
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      p.name || "User",
                    )}&background=0195D3&color=fff&size=56`;
                  }}
                />
              ) : (
                <div className="avatar avatar-sm">{p.initials}</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Name + tag on the same row, left-aligned to each other.
                    flexWrap lets the tag drop below the name on narrow
                    cards instead of overflowing. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    flexWrap: "wrap",
                    rowGap: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--fg)",
                      lineHeight: 1.2,
                    }}
                  >
                    {p.name}
                  </span>
                  <span
                    className="badge badge-role"
                    style={{ height: 20, fontSize: 10 }}
                  >
                    {p.tag}
                  </span>
                </div>
                {/* Tightened line-height + tiny top margin so the
                    timestamp sits flush under the name rather than
                    leaving a visible gap from default line-box padding. */}
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--fg-3)",
                    lineHeight: 1.2,
                    marginTop: 2,
                  }}
                >
                  {p.when} ago
                </div>
              </div>
            </div>

            {p.title && (
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: "var(--brand-ink)",
                  letterSpacing: "-0.005em",
                  marginBottom: 4,
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {p.title}
              </div>
            )}
            {p.content && (
              <>
                <div
                  style={{
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: "var(--fg-2)",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    marginBottom: getVisibleTextLength(p.content) > 150 ? 4 : 10,
                  }}
                >
                  {typeof p.content === "string" && /<\/?[a-z][\s\S]*>/i.test(p.content) ? (
                    <Linkify>
                      <div className="rich-content">
                        {parse(sanitizePreviewContent(p.content))}
                      </div>
                    </Linkify>
                  ) : (
                    p.content
                  )}
                </div>
                {/* Heuristic: anything past ~150 chars almost always
                    overflows 3 lines at 13px in this card width, so show
                    Read More. stopPropagation isn't strictly required
                    (the whole card also navigates to the same place) but
                    keeps the click semantics explicit. */}
                {getVisibleTextLength(p.content) > 150 && p.id && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/community/${p.id}`);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--brand-600)",
                      cursor: "pointer",
                      marginBottom: 10,
                    }}
                  >
                    Read More
                  </button>
                )}
              </>
            )}
            {p.image && (
              <div
                style={{
                  marginBottom: 10,
                  borderRadius: 10,
                  overflow: "hidden",
                  border: "1px solid var(--border)",
                  background: "var(--bg-muted)",
                }}
              >
                <img
                  src={p.image}
                  alt={p.title || p.name || "Community post"}
                  loading="lazy"
                  style={{
                    display: "block",
                    width: "100%",
                    maxHeight: 180,
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    // Hide the wrapper if the asset 404s — avoids
                    // showing a broken-image icon to the user.
                    e.currentTarget.parentElement.style.display = "none";
                  }}
                />
              </div>
            )}

            <div
              // First post's action row is the tour target for the
              // like / comment / save icons.
              data-tour={i === 0 ? "target-community-actions" : undefined}
              style={{
                display: "flex",
                gap: 16,
                fontSize: 12,
                color: "var(--fg-3)",
              }}
            >
              <div
                style={{ position: "relative", display: "inline-block" }}
                onMouseEnter={() => openPicker(p.id)}
                onMouseLeave={schedulePickerClose}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    // Card has its own onClick that navigates to the
                    // detail page — stop it so reacting stays in-place.
                    e.stopPropagation();
                    handleSetReaction(p.id, "like");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                    }
                  }}
                  aria-pressed={Boolean(p.userReaction)}
                  aria-label={
                    p.userReaction ? `Remove ${p.userReaction}` : "Like post"
                  }
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    color: p.userReaction ? "var(--brand-600)" : "var(--fg-3)",
                    fontWeight: p.userReaction ? 700 : 400,
                    cursor: "pointer",
                    fontSize: "inherit",
                  }}
                >
                  {reactionIconFor(p.userReaction, Boolean(p.userReaction))}
                  {p.likes > 0 && <span>{p.likes}</span>}
                </button>
                {pickerOpenFor === p.id && (
                  <div
                    className="reaction-picker-popup"
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={() => openPicker(p.id)}
                    onMouseLeave={schedulePickerClose}
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: 0,
                      zIndex: 1000,
                      marginBottom: 8,
                    }}
                  >
                    <FacebookSelector
                      iconSize={22}
                      onSelect={(type) => {
                        setPickerOpenFor(null);
                        handleSetReaction(p.id, type);
                      }}
                    />
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  // Stop the wrapping card's onClick from navigating
                  // away — we want to open the modal in place.
                  e.stopPropagation();
                  openCommentModal(p.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                aria-label={`Open comments (${p.comments})`}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  color: p.comments > 0 ? "var(--brand-600)" : "var(--fg-3)",
                  fontWeight: p.comments > 0 ? 700 : 400,
                  cursor: "pointer",
                  fontSize: "inherit",
                }}
              >
                <Icon.bubble style={{ width: 13, height: 13 }} />
                {p.comments > 0 && <span>{p.comments}</span>}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleSave(p.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                disabled={p.saveLoading}
                aria-pressed={Boolean(p.isSaved)}
                aria-label={p.isSaved ? "Unsave post" : "Save post"}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  color: p.isSaved ? "var(--brand-600)" : "var(--fg-3)",
                  cursor: p.saveLoading ? "default" : "pointer",
                  fontSize: "inherit",
                  opacity: p.saveLoading ? 0.6 : 1,
                }}
              >
                <Bookmark
                  size={13}
                  fill={p.isSaved ? "currentColor" : "none"}
                />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal
        show={Boolean(commentModalPostId)}
        onHide={closeCommentModal}
        size="lg"
        centered
        dialogClassName="custom-modal profile-other-details-modal dashboard-comments-modal"
      >
        {/* <Modal.Header closeButton>
          <Modal.Title
            id="dashboard-comments-modal-title"
            style={{ fontSize: 16, fontWeight: 700 }}
          >
            Comments
          </Modal.Title>
        </Modal.Header> */}
        <Modal.Header>
          <h5 className="modal-title">Comments</h5>
          <Button className="close-btn" onClick={closeCommentModal}>
            <FiX size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body style={{ padding: "12px 16px 16px" }}>
          {commentModalPostId && (
            <CommentsSection
              postId={commentModalPostId}
              onCommentCountUpdate={handleCommentCountUpdate}
            />
          )}
        </Modal.Body>
      </Modal>

      <RestrictionModal
        isVisible={showSaveRestriction}
        onClose={() => setShowSaveRestriction(false)}
        eventSource="save_limit"
        iconType="bookmark"
        heading="Want to save this post?"
        subText="Upgrade to Pro to bookmark posts and find them later."
        infoText="Pro users can save unlimited community posts to revisit anytime."
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   7 · TOOLS
   ════════════════════════════════════════════════════════════ */
function ToolCard({ icon, title, sub, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "var(--white)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: "14px 14px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "var(--brand-50)",
          color: "var(--brand-600)",
          display: "grid",
          placeItems: "center",
        }}
      >
        {icon}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "var(--brand-ink)",
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{sub}</div>
    </button>
  );
}

function ToolsSection() {
  const navigate = useNavigate();
  return (
    <div style={{ marginBottom: 12 }}>
      <SectionTitle
        title="Featured tools"
        action={
          <button
            onClick={() => navigate("/salaries-paychecks")}
            data-tour="target-tools-all"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--brand-600)",
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
            >
              View all calculators
              <Icon.arrowR style={{ width: 13, height: 13 }} />
            </span>
          </button>
        }
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          type="button"
          onClick={() => navigate("/comparison")}
          style={{
            position: "relative",
            background:
              "linear-gradient(135deg, var(--brand-700) 0%, var(--brand-500) 100%)",
            color: "#fff",
            border: "none",
            borderRadius: 14,
            padding: "14px 16px",
            textAlign: "left",
            cursor: "pointer",
            overflow: "hidden",
            boxShadow: "var(--shadow-brand)",
          }}
        >
          <svg
            viewBox="0 0 200 100"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              right: -10,
              top: -10,
              width: 200,
              height: 120,
              opacity: 0.18,
              pointerEvents: "none",
            }}
          >
            <path
              d="M30,80 Q60,20 110,40 T190,30"
              fill="none"
              stroke="#fff"
              strokeWidth="1"
              strokeDasharray="2 4"
            />
            <circle cx="30" cy="80" r="6" fill="#fff" />
            <circle cx="190" cy="30" r="8" fill="#fff" />
            <path
              d="M186 22 a8 8 0 1 1 8 8"
              fill="none"
              stroke="#fff"
              strokeWidth="1.5"
            />
          </svg>
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.75)",
                  }}
                >
                  ★ Featured tool
                </span>
              </div>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 800,
                  letterSpacing: "-0.015em",
                  lineHeight: 1.1,
                  marginBottom: 4,
                }}
              >
                Live Here, Work There
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "rgba(255,255,255,0.85)",
                  lineHeight: 1.4,
                  maxWidth: 220,
                }}
              >
                Compare every commute pair — net pay after taxes, rent, and gas.
              </div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(255,255,255,0.18)",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                marginLeft: 8,
              }}
            >
              {Icon.arrowSwap({ style: { width: 18, height: 18, color: "#fff" } })}
            </div>
          </div>
        </button>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          <ToolCard
            icon={<Icon.hospital style={{ width: 18, height: 18 }} />}
            title="Hospital Salary Calculator"
            sub="Pay by employer"
            onClick={() => navigate("/salaries-paychecks")}
          />
          <ToolCard
            icon={<Icon.home style={{ width: 18, height: 18 }} />}
            title="Mortgage Calculator"
            sub="Estimate monthly payment"
            onClick={() => navigate("/mortgage")}
          />
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   8 · TOP CITIES BY LEFTOVER
   ════════════════════════════════════════════════════════════ */
function TopCities({ onRowClick }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  // Mirrors CitySpotlight: render a shimmering skeleton until the first
  // fetch settles so the section doesn't pop in / jump on load.
  const [hasLoaded, setHasLoaded] = useState(false);
  const authUser = useSelector((state) => state.auth?.user);
  // Send the signed-in user's nursing role so results are tailored to
  // them (authUser.nursing_role.id). Falls back to 9 (Registered Nurses)
  // — same default Search.js uses — if the user hasn't picked a role.
  const roleId = authUser?.nursing_role?.id;
  // Fetch top 5 cities by after_rent (no pagination). The filterCity
  // thunk normalizes the response to { cities, total, page, per_page };
  // we read .cities and map each row to the shape the existing card JSX
  // expects (city/state/hospitals/crime/leftover).
  useEffect(() => {
    let cancelled = false;
    const fetchTop = async () => {
      try {
        const result = await dispatch(
          filterCity({
            fieldName: "after_rent",
            shortBy: "desc",
            per_page: 5,
            page: 1,
            append: false,
            role: [roleId || 9],
            search_value: "",
          }),
        );
        if (cancelled) return;
        const rows = result?.payload?.cities || [];
        const mapped = rows.slice(0, 5).map((r) => ({
          // city id — used by the /search/details/:id route
          id: r.id,
          city: r.city,
          state: r.state,
          hospitals: Number(r.hospital_count ?? 0),
          crime: r.overall_crime_grade || "—",
          leftover: Number(r.after_rent ?? r.after_mortgage ?? 0),
        }));
        setCities(mapped);
      } catch (err) {
        console.error("Failed to load top cities:", err);
      } finally {
        if (!cancelled) setHasLoaded(true);
      }
    };
    fetchTop();
    return () => {
      cancelled = true;
    };
  }, [dispatch, roleId]);

  return (
    <div style={{ marginBottom: 12 }}>
      <SectionTitle
        title="Top cities by leftover"
        subtitle={`${shortRole(
          authUser?.nursing_role?.role_title,
        )} · ${shortSpecialty(
          authUser?.nurseProfile?.primary_specialty,
        )} · monthly`}
      />
      <div
        className="mmp-tc__list"
        data-tour="target-topcities-list"
        style={{
          background: "var(--white)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          overflowX: "hidden",
        }}
      >
        {!hasLoaded && (
          <div className="mmp-tc-sk" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="mmp-tc-sk__row">
                <span
                  className="sk"
                  style={{ width: 26, height: 26, borderRadius: 8 }}
                />
                <div className="mmp-tc-sk__main">
                  <span className="sk" style={{ width: "55%", height: 13 }} />
                  <span
                    className="sk"
                    style={{ width: "35%", height: 10, marginTop: 6 }}
                  />
                </div>
                <div className="mmp-tc-sk__amount">
                  <span
                    className="sk"
                    style={{ width: 70, height: 15, marginLeft: "auto" }}
                  />
                  <span
                    className="sk"
                    style={{
                      width: 48,
                      height: 9,
                      marginTop: 5,
                      marginLeft: "auto",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        {cities.map((c, i) => {
          const isFirst = i === 0;
          return (
            <div
              key={c.id ?? c.city}
              className="mmp-tc__row"
              // Only the top-ranked row carries the tour hook, so the
              // walkthrough has a single stable target to highlight.
              data-tour={isFirst ? "target-topcities-row" : undefined}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!c.id) return;
                if (onRowClick) {
                  onRowClick(c);
                } else {
                  navigate(`/search/details/${c.id}`);
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && c.id) {
                  e.preventDefault();
                  if (onRowClick) {
                    onRowClick(c);
                  } else {
                    navigate(`/search/details/${c.id}`);
                  }
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 14px",
                borderTop: i === 0 ? "none" : "1px solid var(--divider)",
                background: isFirst
                  ? "linear-gradient(90deg, var(--brand-50) 0%, transparent 60%)"
                  : "transparent",
                cursor: c.id ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  background: isFirst ? "var(--brand-500)" : "var(--bg-muted)",
                  color: isFirst ? "#fff" : "var(--fg-2)",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 13,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "var(--brand-ink)",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {c.city}
                  </span>
                  <span
                    className="badge badge-state"
                    style={{ height: 18, fontSize: 10, padding: "0 5px" }}
                  >
                    {c.state}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    fontSize: 11,
                    color: "var(--fg-3)",
                    marginTop: 1,
                    lineHeight: 1.2,
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Icon.hospital style={{ width: 11, height: 11 }} />
                    {c.hospitals}
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span
                      className={`grade grade-${c.crime.toLowerCase()}`}
                      style={{ width: 16, height: 16, fontSize: 9 }}
                    >
                      {c.crime}
                    </span>
                  </span>
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div
                  className="t-num mmp-tc__amount"
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: "var(--brand-600)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  ${c.leftover.toLocaleString()}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "var(--fg-3)",
                    fontWeight: 500,
                    marginTop: 2,
                  }}
                >
                  /mo leftover
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ════════════════════════════════════════════════════════════ */
const DashboardHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileStore = useSelector((state) => state.profile);
  const authStore = useSelector((state) => state.auth);

  // City-detail paywall — mirrors Search.js so a Free/Plus user who has
  // exhausted their daily detail-view allowance sees the same upgrade
  // modal when tapping View details from the spotlight card.
  const [cityLimitModalVisible, setCityLimitModalVisible] = useState(false);
  const [cityLimitModalData, setCityLimitModalData] = useState({});
  const [upgradeSource, setUpgradeSource] = useState(null);
  const [showSaveCityModal, setShowSaveCityModal] = useState(false);

  const BOOKMARK_PAGE_SIZE = 6;
  const [savedCities, setSavedCities] = useState([]);
  const [bookmarkPage, setBookmarkPage] = useState(1);
  const [bookmarkTotal, setBookmarkTotal] = useState(0);
  const [loadingMoreBookmarks, setLoadingMoreBookmarks] = useState(false);
  // Incremented each time refreshBookmarks loads page 1 so YourCities can
  // reset its horizontal scroll position back to the left edge.
  const [bookmarkScrollKey, setBookmarkScrollKey] = useState(0);
  // Bumped after a bookmark toggle so the CitySpotlight component
  // refetches /city-spotlight and shows updated is_bookmarked state.
  const [spotlightRefreshKey, setSpotlightRefreshKey] = useState(0);
  // Live count of open jobs in the user's living city/state — drives both
  // the New Jobs badge ("X open in {city}") and the welcome line ("X open
  // jobs nearby"). 0 until the API resolves.
  const [nearbyJobsCount, setNearbyJobsCount] = useState(0);
  // Ref-based in-flight guard. Scroll events fire faster than React can
  // re-render, so a state-based `if (loadingMoreBookmarks) return` reads
  // a stale closure value and lets duplicate fetches through (you'd see
  // page 2 fire 2x, page 3 fire 3x, etc). The ref updates synchronously
  // so the next event sees the in-flight state immediately.
  const loadingMoreBookmarksRef = useRef(false);

  // Map an API bookmark row to the shape YourCities expects. Field names
  // mirror what Search.js reads off the same bookmark response.
  const mapBookmark = (b) => ({
    // Top-level `id` is the city id used by the detail route
    // (/search/details/:id) — NOT bookmark_id, which is the join row.
    id: b.id,
    city: b.city,
    state: b.state,
    // Role is needed so the remove button can re-POST the same
    // (city, state, role) triple that originally bookmarked the row.
    role: b.role,
    // Show the monthly rent-side leftover (positive figure) instead of
    // the owning/annual one — which was producing negative values like
    // "$-3,268.83" on the card because it reflected annual mortgage cost.
    leftover: Number(
      b.leftover_renting_monthly ?? b.after_rent ?? b.left_over ?? 0,
    ),
    hospitals: Number(b.hospital_count ?? 0),
    crime: b.overall_crime_grade || b.app_display || "—",
    // Per-city weekly summary; YourCities rotates the displayed
    // indicator through these 3 slots every 3 seconds.
    summary: b.summary || {},
  });

  // Reusable bookmarks fetch — called on mount AND after a bookmark
  // toggle so YourCities reflects the new state without a page reload.
  const refreshBookmarks = React.useCallback(async () => {
    try {
      const result = await dispatch(
        getBookmarks({ page: 1, limit: BOOKMARK_PAGE_SIZE }),
      );
      const rows = result?.payload?.data?.data || [];
      const total = Number(result?.payload?.data?.total ?? rows.length);
      setSavedCities(rows.map(mapBookmark));
      setBookmarkTotal(total);
      setBookmarkPage(1);
      setBookmarkScrollKey((k) => k + 1);
    } catch (err) {
      console.error("Failed to load bookmarked cities:", err);
    }
    // mapBookmark is a stable closure that only depends on identity-
    // stable values, so we deliberately omit it from the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    refreshBookmarks();
  }, [refreshBookmarks]);

  // Fired by YourCities when its internal scroll nears the bottom. Guards
  // against duplicate in-flight calls and against requesting pages past
  // the known total.
  const loadMoreBookmarks = async () => {
    if (loadingMoreBookmarksRef.current) return;
    if (savedCities.length >= bookmarkTotal) return;
    loadingMoreBookmarksRef.current = true;
    setLoadingMoreBookmarks(true);
    try {
      const nextPage = bookmarkPage + 1;
      const result = await dispatch(
        getBookmarks({ page: nextPage, limit: BOOKMARK_PAGE_SIZE }),
      );
      const rows = result?.payload?.data?.data || [];
      if (rows.length === 0) return;
      setSavedCities((prev) => [...prev, ...rows.map(mapBookmark)]);
      setBookmarkPage(nextPage);
    } catch (err) {
      console.error("Failed to load more bookmarked cities:", err);
    } finally {
      loadingMoreBookmarksRef.current = false;
      setLoadingMoreBookmarks(false);
    }
  };

  const authUser = useSelector((state) => state.auth?.user);
  // Plan-badge upgrade modal (opened by tapping the plan shield in the greeting)
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Superseded by activeProfileNudge below (unified shared-util nudge).
  // Kept commented for reference alongside the old .mmp-profile JSX.
  // const profileCompletion =
  //   profileStore?.profileItem?.data?.profile_completed_percentage ??
  //   authUser?.profile_completed_percentage;

  // Dismissal is in-memory only (not persisted) so the nudge reappears on
  // reload, re-login, or a fresh tab — not just once ever.
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(false);
  const dashboardProfileData = profileStore?.profileItem?.data;
  const profileCompletionPercent = getProfileCompletionPercent(
    dashboardProfileData,
  );
  const activeProfileNudge = dashboardProfileData
    ? DASHBOARD_RELEVANT_PROFILE_NUDGES.find((nudge) => {
      const field = PROFILE_COMPLETION_FIELDS_BY_KEY[nudge.key];
      return field && !field.isComplete(dashboardProfileData);
    })
    : null;
  const dismissProfileNudge = () => {
    setProfileNudgeDismissed(true);
  };

  // Fetch the open-jobs count for the user's living city/state. Drives
  // the "X open jobs nearby" welcome line and the "X open in {city}"
  // badge on the New Jobs section. Skipped until both fields are known
  // so we don't fire a useless `?state=&city=` request.
  useEffect(() => {
    const city = authUser?.city;
    const state = authUser?.living_state;
    if (!city || !state) return;
    let cancelled = false;
    dispatch(getJobsbyStateAndCityData({ city, state }))
      .then((result) => {
        if (cancelled) return;
        const rows = result?.payload?.data?.data || [];
        setNearbyJobsCount(Array.isArray(rows) ? rows.length : 0);
      })
      .catch((err) => {
        console.error("Failed to load nearby jobs count:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, authUser?.city, authUser?.living_state]);

  // Bookmark toggle from the City Spotlight heart icon. POSTs to
  // /city-bookmarks/toggle with { city, state, role }, then in parallel:
  //   - bumps spotlightRefreshKey so CitySpotlight refetches and
  //     reflects the new is_bookmarked state on its cards
  //   - refetches /city-bookmarks so the YourCities list reflects the
  //     add/remove without a manual reload.
  const handleSaveCity = async (city) => {
    if (!city?.city || !city?.state) return;
    try {
      await dispatch(
        searchBookmark({
          city: city.city,
          state: city.state,
          role: city.role || authUser?.nursing_role?.role_title,
        }),
      ).unwrap();
      setSpotlightRefreshKey((k) => k + 1);
      refreshBookmarks();
    } catch (err) {
      if (err?.status === 403) {
        setShowSaveCityModal(true);
      } else {
        console.error("Failed to toggle bookmark:", err);
      }
    }
  };

  // View Details from the City Spotlight card. Mirrors Search.js's
  // handleSearchClick: Free/Plus users hit /check-city-count first, and
  // once they've exhausted their daily detail-view allowance the
  // CityDetailLimitModal opens instead of the detail page. Pro users
  // (and any other plan slug) navigate straight through.
  //
  // planSlug is sourced from authStore first, profileStore second —
  // profileStore is populated by getViewProfile() which kicks off in
  // useLayoutEffect and can lag behind a fast user click; authStore is
  // hydrated at login. Without this fallback the if-block silently
  // skipped on early clicks and let the user through unchecked.
  //
  // The limit-hit check is OR-based across the three indicators rather
  // than AND-based: when the API rejects an increment it doesn't always
  // set all three flags consistently, and a strict AND lets some
  // limit-hit responses fall through to navigate.
  const handleViewDetails = async (city) => {
    if (!city?.id) return;
    const planSlug = (
      authStore?.user?.plan?.slug ||
      profileStore?.profileItem?.data?.plan?.slug ||
      ""
    )
      .toString()
      .toLowerCase();

    if (planSlug === "free") {
      try {
        const result = await dispatch(
          checkFilterCityCount({
            plan_slug: planSlug,
            increment_count: true,
            city_id: city.id,
          }),
        );
        const restrictionData = result?.payload?.checkFilterCityCountData;

        const usedCount = Number(restrictionData?.used_count ?? 0);
        const maxCount = Number(restrictionData?.comparisanMaxCount ?? 0);
        const limitHit =
          restrictionData?.is_comparisan === false ||
          restrictionData?.incrementSuccess === false ||
          (maxCount > 0 && usedCount >= maxCount);

        if (limitHit) {
          setUpgradeSource("city_detail_limit");
          setCityLimitModalData({});
          setCityLimitModalVisible(true);
          return;
        }
      } catch (err) {
        console.error("Failed to check city detail limit:", err);
      }
    }

    // Tag the navigation with from=dashboard so the search-detail back
    // arrow returns to the dashboard instead of falling back to /search.
    // Shared by City Spotlight, Your Cities, and Top Cities by Leftover.
    navigate(`/search/details/${city.id}`, { state: { from: "dashboard" } });
  };

  // Remove a saved city from YourCities. searchBookmark toggles, so
  // POSTing the same (city, state, role) triple un-bookmarks it. Then
  // refresh both the bookmarks list AND CitySpotlight so the heart on
  // any matching spotlight card flips too.
  const handleRemoveSavedCity = async (city) => {
    if (!city?.city || !city?.state) return;
    try {
      await dispatch(
        searchBookmark({
          city: city.city,
          state: city.state,
          role: city.role || authUser?.nursing_role?.role_title,
        }),
      );
      refreshBookmarks();
      setSpotlightRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Failed to remove bookmark:", err);
    }
  };

  /* ─── tracking (preserved from previous implementation) ─── */



  useEffect(() => {
    dispatch(activityStart({ page_name: "DASHBOARD", platform: "web" }));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(activityEnd({ page_name: "DASHBOARD" }));
    };
  }, [dispatch]);

  usePageTracking("DASHBOARD");

  useLayoutEffect(() => {
    dispatch(getViewProfile());
  }, [dispatch]);

  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour < 12) {
      return "Good morning";
    } else if (hour < 18) {
      return "Good afternoon";
    } else {
      return "Good evening";
    }
  };

  const dashboardTourSteps = React.useMemo(() => getDashboardTourSteps(), []);
  const { isOpen: isTourOpen, finish: finishTour } = useTour("dashboard");

  return (
    <>
      <NoIndexSEO {...(seoConfig?.dashboard || {})} />
      <div className="mmp-app">
        {/* ─── Scrolling content ─── */}
        <div className="mmp-scroll">
          {/* Commented out — replaced by the unified profile-completion
              nudge below (uses shared utils/profileCompletion instead of
              the backend's coarser profile_completed_percentage field, so
              the message matches Jobs/Search/Paycheck/Salaries/Mortgage/
              Comparison). Kept for reference.
          {profileCompletion != null && profileCompletion < 75 ? (
            <section className="mmp-sect mmp-sect--profile">
              <div
                className="mmp-profile"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigate("/registration", {
                    state: {
                      fromProfile: true,
                      name: profileStore?.profileItem?.data?.full_name,
                      email: profileStore?.profileItem?.data?.email,
                    },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate("/registration", {
                      state: {
                        fromProfile: true,
                        name: profileStore?.profileItem?.data?.full_name,
                        email: profileStore?.profileItem?.data?.email,
                      },
                    });
                  }
                }}
              >
                <div className="mmp-profile__head">
                  <div className="mmp-profile__title">
                    <span className="mmp-profile__eyebrow">Complete your profile</span>
                    <span className="mmp-profile__sub">
                      Unlock more accurate and personalized insights.
                    </span>
                  </div>
                  <div className="mmp-profile__pct">
                    {profileCompletion ?? 0}%
                  </div>
                </div>
                <div className="mmp-profile__track">
                  <div
                    className="mmp-profile__fill"
                    style={{ width: `${profileCompletion ?? 0}%` }}
                  />
                </div>
              </div>
            </section>
          ) : (
            ""
          )}
          */}

          {activeProfileNudge && !profileNudgeDismissed && (
            <section className="mmp-sect mmp-sect--profile-nudge">
              <div
                className="mmp-profile-nudge"
                role="button"
                tabIndex={0}
                onClick={() => navigate("/profile")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate("/profile");
                  }
                }}
              >
                <div className="mmp-profile-nudge__icon">
                  <UserRound />
                </div>
                <div className="mmp-profile-nudge__body">
                  <div className="mmp-profile-nudge__head">
                    <span className="mmp-profile-nudge__eyebrow">
                      Complete your profile
                    </span>
                    <span className="mmp-profile-nudge__pct">
                      {profileCompletionPercent}%
                    </span>
                  </div>
                  <div className="mmp-profile-nudge__bar-track">
                    <div
                      className="mmp-profile-nudge__bar-fill"
                      style={{ width: `${profileCompletionPercent}%` }}
                    />
                  </div>
                  <span className="mmp-profile-nudge__text">
                    {activeProfileNudge.message}
                  </span>
                </div>
                <button
                  type="button"
                  className="mmp-profile-nudge__close"
                  aria-label="Dismiss"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissProfileNudge();
                  }}
                >
                  ×
                </button>
              </div>
            </section>
          )}
          <section className="mmp-sect mmp-sect--welcome">
            <div className="mmp-welcome">
              {/* Plan badge + upgrade-modal click commented out per request
              <div
                className="mmp-welcome__eyebrow"
                role="button"
                tabIndex={0}
                onClick={() => setShowUpgrade(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowUpgrade(true);
                  }
                }}
                style={{ cursor: "pointer" }}
                title="View upgrade options"
              >
                ◆ {getGreeting()}, {authUser?.full_name}
                <PlanShield
                  planName={authUser?.plan?.name}
                  onClick={() => setShowUpgrade(true)}
                />
              </div>
              */}
              <div className="mmp-welcome__eyebrow">
                ◆ {getGreeting()}, {authUser?.full_name}
              </div>
              <div className="mmp-welcome__meta">
                <span>{shortRole(authUser?.nursing_role?.role_title)}</span>
                <span className="mmp-welcome__dot">·</span>
                <span>
                  {shortSpecialty(authUser?.nurseProfile?.primary_specialty)}
                </span>
                <span className="mmp-welcome__dot">·</span>
                <span className="mmp-welcome__loc">
                  {authUser?.city || "NA"}, {authUser?.living_state || "NA"}
                </span>
                {/* <span className="mmp-welcome__dot">·</span>
                <a className="mmp-welcome__jobs">
                  {nearbyJobsCount} open jobs nearby
                </a> */}
              </div>
            </div>
          </section>
          <div className="mmp-twocol">
            <div className="mmp-twocol__left">
              <section
                className="mmp-sect mmp-sect--spotlight"
                data-tour="card-spotlight"
              >
                <CitySpotlight
                  savedCities={savedCities}
                  onSave={handleSaveCity}
                  onViewDetails={handleViewDetails}
                  refreshKey={spotlightRefreshKey}
                />
              </section>
              <section
                className="mmp-sect mmp-sect--jobs"
                data-tour="card-jobs"
              >
                <NewJobs
                  nearbyJobsCount={nearbyJobsCount}
                  nearbyCity={authUser?.city || ""}
                />
              </section>
              <section className="mmp-sect mmp-sect--map" data-tour="card-map">
                <HospitalMap />
              </section>
              <section
                className="mmp-sect mmp-sect--tools"
                data-tour="card-tools"
              >
                <ToolsSection />
              </section>
            </div>
            <div className="mmp-twocol__right">
              <section
                className="mmp-sect mmp-sect--your-cities"
                data-tour="card-your-cities"
              >
                <YourCities
                  cities={savedCities}
                  total={bookmarkTotal}
                  hasMore={savedCities.length < bookmarkTotal}
                  loadingMore={loadingMoreBookmarks}
                  onLoadMore={loadMoreBookmarks}
                  onRemove={handleRemoveSavedCity}
                  onCardClick={handleViewDetails}
                  scrollResetKey={bookmarkScrollKey}
                />
              </section>
              <section
                className="mmp-sect mmp-sect--homeowner"
                data-tour="card-homeowner"
              >
                <HomeownershipSnapshot />
              </section>
              <section
                className="mmp-sect mmp-sect--community"
                data-tour="card-community"
              >
                <CommunitySection />
              </section>
              <section
                className="mmp-sect mmp-sect--top-cities"
                data-tour="card-top-cities"
              >
                <TopCities onRowClick={handleViewDetails} />
              </section>
            </div>
          </div>
          <div style={{ height: 8 }} />
        </div>
      </div>
      <CityDetailLimitModal
        isVisible={cityLimitModalVisible}
        onClose={() => setCityLimitModalVisible(false)}
        eventSource={upgradeSource}
        heading={cityLimitModalData.heading}
        infoText={cityLimitModalData.infoText}
      />

      <RestrictionModal
        isVisible={showSaveCityModal}
        onClose={() => setShowSaveCityModal(false)}
        eventSource="save_city"
        iconType="search-pin"
        thirdCtaMode="newsletter"
        heading="Want to save this city?"
        subText="Upgrade to Pro to save cities and track them in Your Cities."
        infoText="Free users can save 1 city per day. Pro users can save unlimited cities and get personalized insights for each one."
      />

      <GuidedTour
        isOpen={isTourOpen}
        steps={dashboardTourSteps}
        onDone={finishTour}
      />

      {/* Plan-badge upgrade modal commented out per request
      <UpgradePlanModal
        show={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        source="dashboard_plan_badge"
      />
      */}
    </>
  );
};

export default DashboardHome;
